import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  BarChart3,
  Building2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Download,
  Eye,
  EyeOff,
  FileText,
  GripVertical,
  Landmark,
  LayoutList,
  MessageSquare,
  Palette,
  Plus,
  Presentation,
  Printer,
  RefreshCw,
  RotateCcw,
  Save,
  Settings,
  Share2,
  Sparkles,
  Trash2,
  TrendingUp,
  WalletCards,
  X,
} from 'lucide-react'
import {
  getModelIdForProvider,
  providerForAiRequest,
  readAiSettings,
} from '../../ai/settings'
import type {
  MetricComparisonResponse,
  QuickBooksCfoPresentationAiDraftResponse,
  QuickBooksCfoPresentationResponse,
  QuickBooksArTrendAnalysis,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
  ServerApiClient,
} from '../../data/ServerApiClient'
import { CfoSharingPanel } from './CfoSharingPanel'
import { buildRatioCardBlockModel, buildRatioCardSparkline, formatRatioCardValue } from './cfo/presentation/ratioCardBlock'
import { cfoCurrencyCode, cfoCurrencySymbol } from './cfo/cfoFormat'
import { buildQuickBooksCfoPresentationDeckHtml, buildQuickBooksCfoPresentationHtml, buildQuickBooksCfoPresentationPdfHtml, serverWaterfallBridge } from './QuickBooksCfoPresentationExport'
import { visiblePresentationStatementRows } from './quickBooksCfoPresentationStatementRows'
import { KeyHighlightsSection } from './cfo/visuals/KeyHighlightsSection'
import {
  PRESENTATION_TEMPLATES,
  PRESENTATION_THEMES,
  applyTemplateToReportBlocks,
  resolvePresentationTheme,
  type PresentationTemplateId,
  type PresentationThemeId,
} from './quickBooksCfoPresentationThemes'
import './QuickBooksCfoPresentationTab.css'

type PresentationSectionKey = 'home' | 'profitAndLoss' | 'balanceSheet' | 'cashFlow' | 'accountsReceivable' | 'actions'
type PresentationWaterfallMode = 'mom' | 'yoy' | 'ytd'
type PresentationReportBlockKind = 'key_highlights' | 'text' | 'kpi_grid' | 'statement_rollup' | 'statement_detail' | 'trend_chart' | 'waterfall' | 'ar_summary' | 'action_list' | 'ratio_card'

type PresentationReportBlock = {
  id: string
  kind: PresentationReportBlockKind
  title: string
  section: PresentationSectionKey
  enabled?: boolean
  source?: Record<string, any>
  layout?: Record<string, any>
  style?: Record<string, any>
  content?: Record<string, any>
}

type PresentationAgentTarget = {
  section: PresentationSectionKey
  blockId: string
  title: string
}

type PresentationBrandingConfig = {
  reportTitle?: string
  subtitle?: string
  companyDisplayName?: string
  preparedReportTitle?: string
  preparedReportIcon?: string
}

type PresentationSectionConfig = {
  enabled?: boolean
  title?: string
  aiEnabled?: boolean
  defaultPrompt?: string
  promptHints?: string
  promptAvoid?: string
}

type PresentationWaterfallConfig = {
  id: string
  title: string
  mode: PresentationWaterfallMode
  enabled?: boolean
  aiEnabled?: boolean
  promptHints?: string
  promptAvoid?: string
}

type PresentationDerivedMetricsConfig = {
  ebitda?: {
    enabled?: boolean
    interestEnabled?: boolean
    taxesEnabled?: boolean
    depreciationEnabled?: boolean
    amortizationEnabled?: boolean
    customRowHints?: string[]
  }
}

type PresentationConfig = {
  version?: number
  template?: string
  reportTitle?: string
  branding?: PresentationBrandingConfig
  periodCount?: number
  asOfDate?: string
  sections?: Record<string, boolean>
  reportBlocks?: PresentationReportBlock[]
  sectionConfig?: Partial<Record<PresentationSectionKey, PresentationSectionConfig>>
  waterfalls?: PresentationWaterfallConfig[]
  derivedMetrics?: PresentationDerivedMetricsConfig
  style?: Record<string, any>
  receivables?: {
    topCustomerLimit?: number
  }
  statementViews?: Record<string, PresentationStatementView>
  targets?: Record<string, any>
  commentary?: {
    executiveSummary?: string
    sectionSummaries?: Record<string, string>
    alerts?: string[]
    actions?: string[]
    dataCaveats?: string[]
    aiMeta?: {
      model_id?: string
      generated_at?: string
      evidence_count?: number
      warnings?: string[]
    }
  }
  sourcePackage?: {
    id?: string
    name?: string
    source?: string
    layout_version_id?: string
    connection_id?: string
    period_count?: number
    as_of_date?: string
    generated_at?: string
  }
}

type PresentationStatementView = {
  id?: string
  name?: string
  source?: string
  statement_type?: string
  statementType?: string
  layout_version_id?: string | null
  layoutVersionId?: string | null
  grain?: string
  period_count?: number
  periodCount?: number
  as_of_date?: string
  asOfDate?: string
  show_percent_of_sales?: boolean
  showPercentOfSales?: boolean
}

type SavedStatementView = {
  id: string
  name: string
  statementType: string
  layoutVersionId?: string
  grain?: string
  periodCount?: number
  asOfDate?: string
  showPercentOfSales?: boolean
}

type Props = {
  appApi: ServerApiClient
  workbookId: string | null
  isEditor?: boolean
}

const DEFAULT_PRESENTATION_PERIOD_COUNT = 6
const QUARTERLY_TREND_MONTH_COUNT = 24
const QUARTERLY_TREND_MAX_QUARTERS = 8

const DEFAULT_CONFIG: PresentationConfig = {
  version: 2,
  template: 'cfo-client-financials',
  reportTitle: 'Financial Review',
  branding: {
    reportTitle: 'Financial Review',
    subtitle: '',
    companyDisplayName: '',
    preparedReportTitle: 'Prepared report',
    preparedReportIcon: 'building',
  },
  periodCount: DEFAULT_PRESENTATION_PERIOD_COUNT,
  sections: {
    home: true,
    profitAndLoss: true,
    balanceSheet: true,
    cashFlow: true,
    accountsReceivable: true,
    actions: true,
  },
  reportBlocks: [
    { id: 'key-highlights', kind: 'key_highlights', title: 'Key Highlights', section: 'home', enabled: true },
    { id: 'home-kpis', kind: 'kpi_grid', title: 'Executive KPIs', section: 'home', enabled: true },
    { id: 'home-summary', kind: 'text', title: 'Executive Summary', section: 'home', enabled: true, source: { commentary: 'executiveSummary' } },
    { id: 'home-waterfalls', kind: 'waterfall', title: 'Net Income Bridges', section: 'home', enabled: true },
    { id: 'home-pnl-rollup', kind: 'statement_rollup', title: 'P&L Rollup', section: 'home', enabled: true, source: { statement_type: 'profit_and_loss' } },
    { id: 'home-quarterly-trend', kind: 'trend_chart', title: 'Quarterly Revenue & Net Income Trend', section: 'home', enabled: true, source: { statement_type: 'profit_and_loss', metric: 'quarterly_revenue_net_income' } },
    { id: 'pnl-kpis', kind: 'kpi_grid', title: 'Profit & Loss KPIs', section: 'profitAndLoss', enabled: true },
    { id: 'pnl-summary', kind: 'text', title: 'Profit & Loss Analysis', section: 'profitAndLoss', enabled: true, source: { commentary_section: 'profitAndLoss' } },
    { id: 'pnl-rollup', kind: 'statement_rollup', title: 'Profit & Loss Rollup', section: 'profitAndLoss', enabled: true, source: { statement_type: 'profit_and_loss' } },
    { id: 'pnl-trend', kind: 'trend_chart', title: 'Rolling Net Income', section: 'profitAndLoss', enabled: true, source: { statement_type: 'profit_and_loss', row_label: 'net income' } },
    { id: 'pnl-quarterly-trend', kind: 'trend_chart', title: 'Quarterly Revenue & Net Income Trend', section: 'profitAndLoss', enabled: true, source: { statement_type: 'profit_and_loss', metric: 'quarterly_revenue_net_income' } },
    { id: 'pnl-detail', kind: 'statement_detail', title: 'Supporting P&L Detail', section: 'profitAndLoss', enabled: true, source: { statement_type: 'profit_and_loss' }, layout: { limit: 32 } },
    { id: 'balance-sheet-kpis', kind: 'kpi_grid', title: 'Balance Sheet KPIs', section: 'balanceSheet', enabled: true },
    { id: 'balance-sheet-summary', kind: 'text', title: 'Balance Sheet Analysis', section: 'balanceSheet', enabled: true, source: { commentary_section: 'balanceSheet' } },
    { id: 'balance-sheet-rollup', kind: 'statement_rollup', title: 'Balance Sheet Rollup', section: 'balanceSheet', enabled: true, source: { statement_type: 'balance_sheet' } },
    { id: 'balance-sheet-detail', kind: 'statement_detail', title: 'Supporting Balance Sheet Detail', section: 'balanceSheet', enabled: true, source: { statement_type: 'balance_sheet' }, layout: { limit: 32 } },
    { id: 'cash-flow-kpis', kind: 'kpi_grid', title: 'Cash Flow KPIs', section: 'cashFlow', enabled: true },
    { id: 'cash-flow-summary', kind: 'text', title: 'Cash Flow Analysis', section: 'cashFlow', enabled: true, source: { commentary_section: 'cashFlow' } },
    { id: 'cash-flow-rollup', kind: 'statement_rollup', title: 'Cash Flow Rollup', section: 'cashFlow', enabled: true, source: { statement_type: 'cash_flow' } },
    { id: 'cash-flow-trend', kind: 'trend_chart', title: 'Cash Movement', section: 'cashFlow', enabled: true, source: { statement_type: 'cash_flow', row_label: 'net change' } },
    { id: 'cash-flow-detail', kind: 'statement_detail', title: 'Supporting Cash Flow Detail', section: 'cashFlow', enabled: true, source: { statement_type: 'cash_flow' }, layout: { limit: 32 } },
    { id: 'ar-summary', kind: 'ar_summary', title: 'A/R Trends', section: 'accountsReceivable', enabled: true },
    { id: 'actions-list', kind: 'action_list', title: 'Actions & Notes', section: 'actions', enabled: true },
  ],
  sectionConfig: {
    home: {
      enabled: true,
      title: 'Home',
      aiEnabled: true,
      defaultPrompt: 'Summarize the overall financial performance, calling out revenue, profitability, cash, and the most important change drivers.',
      promptHints: '',
      promptAvoid: '',
    },
    profitAndLoss: {
      enabled: true,
      title: 'Profit & Loss',
      aiEnabled: true,
      defaultPrompt: 'Explain revenue, gross profit, EBITDA, net income, margin trends, and the largest operating drivers.',
      promptHints: '',
      promptAvoid: '',
    },
    balanceSheet: {
      enabled: true,
      title: 'Balance Sheet',
      aiEnabled: true,
      defaultPrompt: 'Explain liquidity, working capital, liabilities, equity, and any material balance sheet movements.',
      promptHints: '',
      promptAvoid: '',
    },
    cashFlow: {
      enabled: true,
      title: 'Cash Flow',
      aiEnabled: true,
      defaultPrompt: 'Explain operating, investing, and financing cash-flow movements and whether ending cash reconciles to the balance sheet.',
      promptHints: '',
      promptAvoid: '',
    },
    accountsReceivable: {
      enabled: true,
      title: 'A/R Trends',
      aiEnabled: true,
      defaultPrompt: 'Explain days sales outstanding, average accounts receivable, overdue exposure, weighted open-invoice age, and the customers needing collection attention.',
      promptHints: '',
      promptAvoid: '',
    },
    actions: {
      enabled: true,
      title: 'Actions & Notes',
      aiEnabled: true,
      defaultPrompt: 'Suggest grounded priorities and caveats using only the rendered financial statements.',
      promptHints: '',
      promptAvoid: '',
    },
  },
  receivables: {
    topCustomerLimit: 5,
  },
  waterfalls: [
    { id: 'mom-net-income', title: 'MoM Net Income Bridge', mode: 'mom', enabled: true, aiEnabled: true, promptHints: '', promptAvoid: '' },
    { id: 'yoy-net-income', title: 'YoY Net Income Bridge', mode: 'yoy', enabled: true, aiEnabled: true, promptHints: '', promptAvoid: '' },
    { id: 'ytd-net-income', title: 'YTD Net Income Bridge', mode: 'ytd', enabled: true, aiEnabled: true, promptHints: '', promptAvoid: '' },
  ],
  derivedMetrics: {
    ebitda: {
      enabled: true,
      interestEnabled: true,
      taxesEnabled: true,
      depreciationEnabled: true,
      amortizationEnabled: true,
      customRowHints: [],
    },
  },
  statementViews: {
    profit_and_loss: { id: '__default__', name: 'Default (no layout)', source: 'default', statement_type: 'profit_and_loss' },
    balance_sheet: { id: '__default__', name: 'Default (no layout)', source: 'default', statement_type: 'balance_sheet' },
    cash_flow: { id: '__default__', name: 'Default (no layout)', source: 'default', statement_type: 'cash_flow' },
  },
  targets: {},
  commentary: {
    executiveSummary: '',
    sectionSummaries: {},
    alerts: [],
    actions: [],
    dataCaveats: [],
  },
}

const SECTION_DEFS: Array<{ key: PresentationSectionKey; label: string; icon: typeof Building2 }> = [
  { key: 'home', label: 'Home', icon: Building2 },
  { key: 'profitAndLoss', label: 'Profit & Loss', icon: TrendingUp },
  { key: 'balanceSheet', label: 'Balance Sheet', icon: Landmark },
  { key: 'cashFlow', label: 'Cash Flow', icon: WalletCards },
  { key: 'accountsReceivable', label: 'A/R Trends', icon: BarChart3 },
  { key: 'actions', label: 'Actions & Notes', icon: ClipboardList },
]

const REPORT_BLOCK_KIND_LABELS: Array<{ kind: PresentationReportBlockKind; label: string }> = [
  { kind: 'key_highlights', label: 'Key Highlights' },
  { kind: 'text', label: 'Text / commentary' },
  { kind: 'kpi_grid', label: 'KPI grid' },
  { kind: 'statement_rollup', label: 'Statement rollup' },
  { kind: 'statement_detail', label: 'Statement detail' },
  { kind: 'trend_chart', label: 'Trend chart' },
  { kind: 'waterfall', label: 'Net income bridges' },
  { kind: 'ar_summary', label: 'A/R summary' },
  { kind: 'action_list', label: 'Actions & notes' },
  { kind: 'ratio_card', label: 'Ratio card' },
]

const reportBlockKindLabel = (kind: PresentationReportBlockKind): string =>
  REPORT_BLOCK_KIND_LABELS.find((entry) => entry.kind === kind)?.label || kind

const cleanText = (value: any): string => String(value ?? '').trim()

const renderInlineEmphasis = (value: any): React.ReactNode => {
  const text = cleanText(value)
  const parts = text.split('**')
  if (parts.length === 1 || (parts.length - 1) % 2 !== 0) return text
  return parts.map((part, index) => (
    index % 2 === 1 ? <strong key={`${part}-${index}`}>{part}</strong> : part
  ))
}

const PRESENTATION_TITLE_FALLBACK = 'Financial Review'

const displayReportTitle = (value: any): string => {
  const cleaned = cleanText(value)
  if (!cleaned) return PRESENTATION_TITLE_FALLBACK
  if (!/\b(client|cfo)\b/i.test(cleaned)) return cleaned
  const scrubbed = cleaned
    .replace(/\b(client|cfo)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!scrubbed || /^financials?$/i.test(scrubbed) || /^packet$/i.test(scrubbed)) {
    return PRESENTATION_TITLE_FALLBACK
  }
  return scrubbed
}

const normalizeStringArray = (value: any): string[] => (
  Array.isArray(value)
    ? value.map(cleanText).filter(Boolean)
    : []
)

const normalizeAiMeta = (value: any): NonNullable<NonNullable<PresentationConfig['commentary']>['aiMeta']> | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const evidenceCount = Math.floor(Number(value.evidence_count ?? value.evidenceCount ?? 0))
  const meta: NonNullable<NonNullable<PresentationConfig['commentary']>['aiMeta']> = {
    warnings: normalizeStringArray(value.warnings),
  }
  const modelId = cleanText(value.model_id ?? value.modelId)
  const generatedAt = cleanText(value.generated_at ?? value.generatedAt)
  if (modelId) meta.model_id = modelId
  if (generatedAt) meta.generated_at = generatedAt
  if (Number.isFinite(evidenceCount) && evidenceCount > 0) meta.evidence_count = Math.min(80, evidenceCount)
  return Object.keys(meta).some((key) => {
    const entry = (meta as any)[key]
    return Array.isArray(entry) ? entry.length > 0 : entry != null && entry !== ''
  }) ? meta : undefined
}

const sectionDefault = (key: PresentationSectionKey): PresentationSectionConfig => (
  DEFAULT_CONFIG.sectionConfig?.[key] || {}
)

const normalizeSectionConfig = (
  raw: any,
  sections: Record<string, boolean>,
): Partial<Record<PresentationSectionKey, PresentationSectionConfig>> => {
  const src = raw && typeof raw === 'object' ? raw : {}
  return SECTION_DEFS.reduce<Partial<Record<PresentationSectionKey, PresentationSectionConfig>>>((acc, section) => {
    const defaults = sectionDefault(section.key)
    const value = src[section.key] && typeof src[section.key] === 'object' ? src[section.key] : {}
    acc[section.key] = {
      enabled: value.enabled ?? sections[section.key] ?? defaults.enabled ?? true,
      title: cleanText(value.title) || defaults.title || section.label,
      aiEnabled: value.aiEnabled ?? defaults.aiEnabled ?? true,
      defaultPrompt: cleanText(value.defaultPrompt) || defaults.defaultPrompt || '',
      promptHints: cleanText(value.promptHints),
      promptAvoid: cleanText(value.promptAvoid),
    }
    return acc
  }, {})
}

const normalizeDerivedMetrics = (raw: any): PresentationDerivedMetricsConfig => {
  const src = raw && typeof raw === 'object' ? raw : {}
  const ebitda = src.ebitda && typeof src.ebitda === 'object' ? src.ebitda : {}
  const defaults = DEFAULT_CONFIG.derivedMetrics?.ebitda || {}
  return {
    ebitda: {
      enabled: ebitda.enabled ?? defaults.enabled ?? true,
      interestEnabled: ebitda.interestEnabled ?? defaults.interestEnabled ?? true,
      taxesEnabled: ebitda.taxesEnabled ?? defaults.taxesEnabled ?? true,
      depreciationEnabled: ebitda.depreciationEnabled ?? defaults.depreciationEnabled ?? true,
      amortizationEnabled: ebitda.amortizationEnabled ?? defaults.amortizationEnabled ?? true,
      customRowHints: normalizeStringArray(ebitda.customRowHints).slice(0, 24),
    },
  }
}

const normalizeWaterfalls = (raw: any): PresentationWaterfallConfig[] => {
  const defaults = DEFAULT_CONFIG.waterfalls || []
  const entries = Array.isArray(raw) && raw.length ? raw : defaults
  const seen = new Set<string>()
  return entries.map((entry: any, index: number) => {
    const defaultEntry = defaults[index] || defaults[0]
    const mode = entry?.mode === 'yoy' || entry?.mode === 'ytd' || entry?.mode === 'mom'
      ? entry.mode
      : (defaultEntry?.mode || 'mom')
    const baseId = cleanText(entry?.id) || `${mode}-net-income-${index + 1}`
    const id = seen.has(baseId) ? `${baseId}-${index + 1}` : baseId
    seen.add(id)
    return {
      id,
      title: cleanText(entry?.title) || defaultEntry?.title || 'Net Income Bridge',
      mode,
      enabled: entry?.enabled !== false,
      aiEnabled: entry?.aiEnabled ?? true,
      promptHints: cleanText(entry?.promptHints),
      promptAvoid: cleanText(entry?.promptAvoid),
    }
  }).slice(0, 8)
}

const normalizeReportBlockKind = (value: any): PresentationReportBlockKind => {
  const kind = cleanText(value)
  if (kind === 'key_highlights' || kind === 'kpi_grid' || kind === 'statement_rollup' || kind === 'statement_detail' || kind === 'trend_chart' || kind === 'waterfall' || kind === 'ar_summary' || kind === 'action_list' || kind === 'ratio_card') return kind
  return 'text'
}

const normalizeReportBlockSection = (value: any): PresentationSectionKey => {
  const section = cleanText(value)
  if (section === 'profitAndLoss' || section === 'balanceSheet' || section === 'cashFlow' || section === 'accountsReceivable' || section === 'actions') return section
  return 'home'
}

const builtInTextBlockSource = (
  id: string,
  kind: PresentationReportBlockKind,
  section: PresentationSectionKey,
  title: string,
): Record<string, string> | undefined => {
  if (kind !== 'text') return undefined
  const titleKey = cleanText(title).toLowerCase()
  if (id === 'home-summary' || (section === 'home' && titleKey === 'executive summary')) {
    return { commentary: 'executiveSummary' }
  }
  if (id === 'pnl-summary') return { commentary_section: 'profitAndLoss' }
  if (id === 'balance-sheet-summary') return { commentary_section: 'balanceSheet' }
  if (id === 'cash-flow-summary') return { commentary_section: 'cashFlow' }
  return undefined
}

const normalizeReportBlocks = (raw: any): PresentationReportBlock[] => {
  const defaults = DEFAULT_CONFIG.reportBlocks || []
  const entries = Array.isArray(raw) && raw.length ? raw : defaults
  const seen = new Set<string>()
  const normalized: PresentationReportBlock[] = entries.map((entry: any, index: number) => {
    const kind = normalizeReportBlockKind(entry?.kind)
    const section = normalizeReportBlockSection(entry?.section)
    const baseId = cleanText(entry?.id) || `${section}-${kind}-${index + 1}`
    const id = seen.has(baseId) ? `${baseId}-${index + 1}` : baseId
    seen.add(id)
    const title = cleanText(entry?.title) || 'Report Block'
    const source = builtInTextBlockSource(id, kind, section, title)
      || (entry?.source && typeof entry.source === 'object' ? entry.source : undefined)
    return {
      id,
      kind,
      title,
      section,
      enabled: entry?.enabled !== false,
      source,
      layout: entry?.layout && typeof entry.layout === 'object' ? entry.layout : undefined,
      style: entry?.style && typeof entry.style === 'object' ? entry.style : undefined,
      content: entry?.content && typeof entry.content === 'object' ? entry.content : undefined,
    }
  }).slice(0, 24)
  if (!normalized.some((block) => block.kind === 'key_highlights')) {
    normalized.unshift({ id: 'key-highlights', kind: 'key_highlights', title: 'Key Highlights', section: 'home', enabled: true })
  }
  return normalized
}

const STATEMENT_VIEW_KEYS = ['profit_and_loss', 'balance_sheet', 'cash_flow'] as const

const defaultStatementView = (statementType: string): PresentationStatementView => ({
  id: '__default__',
  name: 'Default (no layout)',
  source: 'default',
  statement_type: statementType,
})

const normalizeStatementView = (value: any, statementType: string, fallbackPeriodCount: number, fallbackAsOfDate?: string): PresentationStatementView => {
  const src = value && typeof value === 'object' ? value : {}
  const id = cleanText(src.id) || '__default__'
  const layoutVersionId = cleanText(src.layout_version_id ?? src.layoutVersionId)
  const useDefault = id === '__default__' || cleanText(src.source) === 'default' || !layoutVersionId
  const periodCount = Math.max(1, Math.min(24, Math.floor(Number(src.period_count ?? src.periodCount ?? fallbackPeriodCount) || fallbackPeriodCount || DEFAULT_PRESENTATION_PERIOD_COUNT)))
  const asOfDate = /^\d{4}-\d{2}-\d{2}$/.test(cleanText(src.as_of_date ?? src.asOfDate))
    ? cleanText(src.as_of_date ?? src.asOfDate)
    : fallbackAsOfDate
  if (useDefault) {
    return {
      ...defaultStatementView(statementType),
      period_count: periodCount,
      as_of_date: asOfDate,
    }
  }
  return {
    id,
    name: cleanText(src.name) || 'Saved view',
    source: cleanText(src.source) || 'saved',
    statement_type: statementType,
    layout_version_id: layoutVersionId,
    grain: cleanText(src.grain) || 'month',
    period_count: periodCount,
    as_of_date: asOfDate,
    show_percent_of_sales: Boolean(src.show_percent_of_sales ?? src.showPercentOfSales),
  }
}

const savedViewStorageKey = (connectionId: string): string => `fg:qbo:fpa:saved-views:${connectionId}`

const loadSavedStatementViews = (connectionId: string): SavedStatementView[] => {
  if (typeof window === 'undefined' || !connectionId) return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(savedViewStorageKey(connectionId)) || '[]')
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((entry: any) => ({
        id: cleanText(entry?.id),
        name: cleanText(entry?.name),
        statementType: cleanText(entry?.statementType) || 'profit_and_loss',
        layoutVersionId: cleanText(entry?.layoutVersionId),
        grain: cleanText(entry?.grain) || 'month',
        periodCount: Math.max(1, Math.min(24, Math.floor(Number(entry?.periodCount) || DEFAULT_PRESENTATION_PERIOD_COUNT))),
        asOfDate: /^\d{4}-\d{2}-\d{2}$/.test(cleanText(entry?.asOfDate)) ? cleanText(entry?.asOfDate) : undefined,
        showPercentOfSales: Boolean(entry?.showPercentOfSales),
      }))
      .filter((entry) => entry.id && entry.name)
  } catch {
    return []
  }
}

const sourcePackageName = (config: PresentationConfig, response: QuickBooksCfoPresentationResponse | null | undefined): string => {
  const raw = config.sourcePackage && typeof config.sourcePackage === 'object' ? config.sourcePackage : null
  const name = cleanText(raw?.name)
  const cleanedName = name && name !== '__current__' ? name.replace(/^Current settings\s*-\s*/i, '').trim() : ''
  if (cleanedName) return cleanedName
  return cleanText(response?.layout_name) || 'selected report'
}

const presentationReportTitle = (config: PresentationConfig): string =>
  displayReportTitle(config.branding?.reportTitle || config.reportTitle)

const presentationCompanyName = (config: PresentationConfig, response: QuickBooksCfoPresentationResponse | null | undefined): string =>
  cleanText(config.branding?.companyDisplayName) || cleanText(response?.company_name) || 'Client'

const presentationSectionTitle = (config: PresentationConfig, key: PresentationSectionKey): string => {
  const def = SECTION_DEFS.find((section) => section.key === key)
  return cleanText(config.sectionConfig?.[key]?.title) || def?.label || key
}

const preparedReportIconFor = (value: any): typeof Building2 => {
  const key = cleanText(value).toLowerCase()
  if (key === 'file' || key === 'report') return FileText
  if (key === 'chart' || key === 'analytics') return BarChart3
  if (key === 'cash' || key === 'wallet') return WalletCards
  if (key === 'bank') return Landmark
  return Building2
}

const normalizeConfig = (raw: any): PresentationConfig => {
  const src = raw && typeof raw === 'object' ? raw : {}
  const commentary = src.commentary && typeof src.commentary === 'object' ? src.commentary : {}
  const branding = src.branding && typeof src.branding === 'object' ? src.branding : {}
  const sections = src.sections && typeof src.sections === 'object' ? src.sections : {}
  const periodCountRaw = Math.floor(Number(src.periodCount ?? DEFAULT_CONFIG.periodCount) || DEFAULT_CONFIG.periodCount || DEFAULT_PRESENTATION_PERIOD_COUNT)
  const periodCount = Math.max(1, Math.min(24, periodCountRaw))
  const asOfDate = /^\d{4}-\d{2}-\d{2}$/.test(cleanText(src.asOfDate)) ? cleanText(src.asOfDate) : undefined
  const rawStatementViews = src.statementViews && typeof src.statementViews === 'object' ? src.statementViews : {}
  const normalizedSections = {
    ...DEFAULT_CONFIG.sections,
    home: sections.home !== false,
    profitAndLoss: sections.profitAndLoss !== false,
    balanceSheet: sections.balanceSheet !== false,
    cashFlow: sections.cashFlow !== false,
    accountsReceivable: sections.accountsReceivable !== false,
    actions: sections.actions !== false,
  }
  const rawReceivables = src.receivables && typeof src.receivables === 'object' ? src.receivables : {}
  const topCustomerLimit = Math.max(1, Math.min(20, Math.floor(Number(rawReceivables.topCustomerLimit ?? rawReceivables.top_customer_limit ?? 5) || 5)))
  const reportTitle = cleanText(branding.reportTitle) || cleanText(src.reportTitle) || DEFAULT_CONFIG.reportTitle
  const sectionSummaries = commentary.sectionSummaries && typeof commentary.sectionSummaries === 'object'
    ? Object.fromEntries(Object.entries(commentary.sectionSummaries).map(([key, value]) => [key, cleanText(value)]).filter(([, value]) => value))
    : {}
  return {
    ...DEFAULT_CONFIG,
    ...src,
    version: 2,
    reportTitle,
    branding: {
      ...DEFAULT_CONFIG.branding,
      ...branding,
      reportTitle,
      subtitle: cleanText(branding.subtitle),
      companyDisplayName: cleanText(branding.companyDisplayName),
      preparedReportTitle: cleanText(branding.preparedReportTitle) || DEFAULT_CONFIG.branding?.preparedReportTitle,
      preparedReportIcon: cleanText(branding.preparedReportIcon) || DEFAULT_CONFIG.branding?.preparedReportIcon,
    },
    periodCount,
    asOfDate,
    sections: normalizedSections,
    reportBlocks: normalizeReportBlocks(src.reportBlocks),
    sectionConfig: normalizeSectionConfig(src.sectionConfig, normalizedSections),
    waterfalls: normalizeWaterfalls(src.waterfalls),
    derivedMetrics: normalizeDerivedMetrics(src.derivedMetrics),
    receivables: {
      topCustomerLimit,
    },
    statementViews: {
      profit_and_loss: normalizeStatementView(rawStatementViews.profit_and_loss, 'profit_and_loss', periodCount, asOfDate),
      balance_sheet: normalizeStatementView(rawStatementViews.balance_sheet, 'balance_sheet', periodCount, asOfDate),
      cash_flow: normalizeStatementView(rawStatementViews.cash_flow, 'cash_flow', periodCount, asOfDate),
    },
    targets: src.targets && typeof src.targets === 'object' ? src.targets : {},
    style: src.style && typeof src.style === 'object' ? src.style : {},
    commentary: {
      executiveSummary: cleanText(commentary.executiveSummary),
      sectionSummaries,
      alerts: normalizeStringArray(commentary.alerts),
      actions: normalizeStringArray(commentary.actions),
      dataCaveats: normalizeStringArray(commentary.dataCaveats),
      aiMeta: normalizeAiMeta(commentary.aiMeta),
    },
  }
}

const labelToken = (value: any): string => cleanText(value)
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ')

const statementPeriods = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
): { periods: string[]; start: number } => {
  const allPeriods = Array.isArray(statement?.periods) ? statement.periods : []
  const bounded = Math.max(1, Math.min(24, Math.floor(Number(periodCount) || DEFAULT_PRESENTATION_PERIOD_COUNT)))
  const periods = allPeriods.slice(-bounded)
  return { periods, start: Math.max(0, allPeriods.length - periods.length) }
}

const rowSemanticKey = (row: QuickBooksFpaStatementRow | null | undefined): string => {
  const existing = cleanText((row as any)?.semantic_key)
  if (existing) return existing
  const label = labelToken(row?.label)
  if (/^net income$|^net ordinary income$|^net earnings$|^net profit$/.test(label)) return 'net_income'
  if (label === 'gross profit') return 'gross_profit'
  if (/^total revenue$|^total income$|^total sales$/.test(label)) return 'total_revenue'
  if (/^revenue$|^income$|^sales$/.test(label)) return 'revenue'
  if (/^total cost of sales$|^total cost of goods sold$|^total cogs$/.test(label)) return 'total_cost_of_sales'
  if (/^total operating expenses$|^operating expenses$|^total expenses$|^g and a expenses$|^general and administrative expenses$/.test(label)) return 'total_operating_expenses'
  if (/^cash and cash equivalents$|^total cash$|^cash$|^checking$|^savings$/.test(label)) return 'cash'
  if (/^total current assets$/.test(label)) return 'total_current_assets'
  if (/^total assets$/.test(label)) return 'total_assets'
  if (/^total current liabilities$/.test(label)) return 'total_current_liabilities'
  if (/^total liabilities$/.test(label)) return 'total_liabilities'
  if (/^total equity$/.test(label)) return 'total_equity'
  if (/^total liabilities and equity$|^total liabilities equity$|^total liabilities and shareholders equity$/.test(label)) return 'total_liabilities_equity'
  if (/^beginning cash/.test(label)) return 'beginning_cash'
  if (/^ending cash|^cash at end of period|^ending cash balance/.test(label)) return 'ending_cash'
  if (/^net change in cash|^change in cash|^net cash change/.test(label)) return 'net_change_cash'
  if (/^net cash .*operating|^cash from operations|^operating cash flow/.test(label)) return 'net_cash_operating'
  if (/^net cash .*investing|^cash from investing/.test(label)) return 'net_cash_investing'
  if (/^net cash .*financing|^cash from financing/.test(label)) return 'net_cash_financing'
  return ''
}

const findStatementRow = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  candidates: string[],
): QuickBooksFpaStatementRow | null => {
  const rows = (Array.isArray(statement?.rows) ? statement.rows : []).filter((row) => row && row.row_type !== 'spacer')
  const semanticCandidates = new Set(candidates.map(labelToken))
  const labelCandidates = candidates.map(labelToken).filter(Boolean)
  const passes = [
    (row: QuickBooksFpaStatementRow) => semanticCandidates.has(labelToken(rowSemanticKey(row))) && row.is_total,
    (row: QuickBooksFpaStatementRow) => semanticCandidates.has(labelToken(rowSemanticKey(row))),
    (row: QuickBooksFpaStatementRow) => labelCandidates.includes(labelToken(row.label)) && row.is_total,
    (row: QuickBooksFpaStatementRow) => labelCandidates.includes(labelToken(row.label)),
    (row: QuickBooksFpaStatementRow) => row.is_total && labelCandidates.some((candidate) => labelToken(row.label).includes(candidate)),
    (row: QuickBooksFpaStatementRow) => labelCandidates.some((candidate) => labelToken(row.label).includes(candidate)),
  ]
  for (const pass of passes) {
    const match = rows.find(pass)
    if (match) return match
  }
  return null
}

const rowPeriodValues = (
  row: QuickBooksFpaStatementRow | null | undefined,
  start: number,
  count: number,
): Array<number | null> => {
  const values = Array.isArray(row?.values) ? row.values : []
  return Array.from({ length: count }, (_, index) => {
    const value = Number(values[start + index])
    return Number.isFinite(value) ? value : null
  })
}

const zipSeries = (
  a: Array<number | null>,
  b: Array<number | null>,
  op: (left: number, right: number) => number,
): Array<number | null> => a.map((left, index) => {
  const right = b[index]
  return Number.isFinite(Number(left)) && Number.isFinite(Number(right)) ? op(Number(left), Number(right)) : null
})

const ratioSeries = (numerator: Array<number | null>, denominator: Array<number | null>): Array<number | null> => (
  numerator.map((value, index) => {
    const den = Number(denominator[index])
    const num = Number(value)
    return Number.isFinite(num) && Number.isFinite(den) && Math.abs(den) > 0.000001 ? num / den : null
  })
)

const seriesHasValue = (values: Array<number | null>): boolean =>
  values.some((value) => Number.isFinite(Number(value)) && Math.abs(Number(value)) > 0.000001)

const normalizedClassifierKey = (value: any): string => cleanText(value)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '')

const rowClassifierToken = (row: QuickBooksFpaStatementRow | null | undefined): string => [
  row?.qbo_account_type,
  row?.qbo_account_sub_type,
  row?.qbo_classification,
  rowSemanticKey(row),
  row?.label,
].map(labelToken).filter(Boolean).join(' ')

const rowMatchesAddbackGroup = (
  row: QuickBooksFpaStatementRow,
  group: 'interest' | 'taxes' | 'depreciation' | 'amortization',
  customHints: string[] = [],
): boolean => {
  const token = rowClassifierToken(row)
  const typeKey = normalizedClassifierKey(row.qbo_account_type)
  const subTypeKey = normalizedClassifierKey(row.qbo_account_sub_type)
  const hintMatch = customHints.some((hint) => {
    const cleanHint = labelToken(hint)
    return cleanHint && token.includes(cleanHint)
  })
  if (hintMatch) return true
  if (group === 'interest') {
    return /interest expense|interest paid|finance charge|loan interest/.test(token)
      || (typeKey.includes('expense') && subTypeKey.includes('interest'))
  }
  if (group === 'taxes') {
    const looksIncomeTax = /income tax|corporate tax|federal tax|state tax|oregon cat tax|pte tax|tax expense/.test(token)
    const excluded = /payroll tax|sales tax|property tax|excise tax|tax payable/.test(token)
    return looksIncomeTax && !excluded
  }
  if (group === 'depreciation') {
    return /depreciation/.test(token) && !/accumulated depreciation/.test(token)
  }
  if (group === 'amortization') {
    return /amortization/.test(token) && !/accumulated amortization/.test(token)
  }
  return false
}

const addbackSeries = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
  group: 'interest' | 'taxes' | 'depreciation' | 'amortization',
  customHints: string[] = [],
): Array<number | null> => {
  const { periods, start } = statementPeriods(statement, periodCount)
  const rows = (Array.isArray(statement?.rows) ? statement.rows : []).filter((row) => row && row.row_type !== 'spacer')
  const matches = rows.filter((row) => rowMatchesAddbackGroup(row, group, customHints))
  const detailMatches = matches.filter((row) => !row.is_total && !row.is_formula)
  const selected = detailMatches.length ? detailMatches : matches
  if (!selected.length) return Array.from({ length: periods.length }, () => null)
  return Array.from({ length: periods.length }, (_, periodIndex) => {
    let total = 0
    let hasValue = false
    for (const row of selected) {
      const value = Number(row.values?.[start + periodIndex])
      if (Number.isFinite(value)) {
        total += Math.abs(value)
        hasValue = true
      }
    }
    return hasValue ? total : null
  })
}

const calculateEbitdaSeries = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
  config: PresentationDerivedMetricsConfig | undefined,
): Array<number | null> => {
  const { periods, start } = statementPeriods(statement, periodCount)
  const ebitdaConfig = config?.ebitda || {}
  if (ebitdaConfig.enabled === false) return Array.from({ length: periods.length }, () => null)
  const netIncome = rowPeriodValues(findStatementRow(statement, ['net_income', 'net income', 'net earnings', 'net profit']), start, periods.length)
  const customHints = normalizeStringArray(ebitdaConfig.customRowHints)
  const addbacks = [
    ebitdaConfig.interestEnabled !== false ? addbackSeries(statement, periodCount, 'interest', customHints) : null,
    ebitdaConfig.taxesEnabled !== false ? addbackSeries(statement, periodCount, 'taxes', customHints) : null,
    ebitdaConfig.depreciationEnabled !== false ? addbackSeries(statement, periodCount, 'depreciation', customHints) : null,
    ebitdaConfig.amortizationEnabled !== false ? addbackSeries(statement, periodCount, 'amortization', customHints) : null,
  ].filter(Boolean) as Array<Array<number | null>>
  return netIncome.map((income, index) => {
    if (!Number.isFinite(Number(income))) return null
    return Number(income) + addbacks.reduce((sum, series) => sum + (Number(series[index]) || 0), 0)
  })
}

type FinancialRollupRow = {
  key: string
  label: string
  values: Array<number | null>
  kind?: 'amount' | 'percent'
  emphasis?: 'section' | 'subtotal' | 'total' | 'muted'
}

const rollupRow = (
  key: string,
  label: string,
  values: Array<number | null>,
  options: Pick<FinancialRollupRow, 'kind' | 'emphasis'> = {},
): FinancialRollupRow => ({ key, label, values, kind: options.kind || 'amount', emphasis: options.emphasis })

const profitAndLossRollupRows = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
  derivedMetrics?: PresentationDerivedMetricsConfig,
): { periods: string[]; rows: FinancialRollupRow[] } => {
  const { periods, start } = statementPeriods(statement, periodCount)
  const count = periods.length
  const revenue = rowPeriodValues(findStatementRow(statement, ['total_revenue', 'total revenue', 'total income', 'revenue', 'income']), start, count)
  const grossProfit = rowPeriodValues(findStatementRow(statement, ['gross_profit', 'gross profit']), start, count)
  const netIncome = rowPeriodValues(findStatementRow(statement, ['net_income', 'net income', 'net earnings', 'net profit']), start, count)
  const costOfSalesSource = rowPeriodValues(findStatementRow(statement, ['total_cost_of_sales', 'total cost of sales', 'total cost of goods sold', 'total cogs', 'cost of sales']), start, count)
  const operatingExpenseSource = rowPeriodValues(findStatementRow(statement, ['total_operating_expenses', 'total operating expenses', 'operating expenses', 'total expenses', 'g&a expenses', 'general and administrative expenses']), start, count)
  const costOfSales = seriesHasValue(costOfSalesSource) ? costOfSalesSource : zipSeries(revenue, grossProfit, (rev, gp) => rev - gp)
  const operatingExpenses = seriesHasValue(operatingExpenseSource) ? operatingExpenseSource : zipSeries(grossProfit, netIncome, (gp, ni) => gp - ni)
  const ebitda = calculateEbitdaSeries(statement, periodCount, derivedMetrics)
  const rows = [
    rollupRow('revenue', 'Total Revenue', revenue, { emphasis: 'section' }),
    rollupRow('cost-of-sales', 'Cost of Sales', costOfSales),
    rollupRow('gross-profit', 'Gross Profit', grossProfit, { emphasis: 'subtotal' }),
    rollupRow('operating-expenses', 'Operating Expenses', operatingExpenses),
    ...(seriesHasValue(ebitda) ? [rollupRow('ebitda', 'EBITDA', ebitda, { emphasis: 'subtotal' })] : []),
    rollupRow('net-income', 'Net Income', netIncome, { emphasis: 'total' }),
    rollupRow('gross-margin', 'Gross Margin', ratioSeries(grossProfit, revenue), { kind: 'percent', emphasis: 'muted' }),
    rollupRow('net-margin', 'Net Margin', ratioSeries(netIncome, revenue), { kind: 'percent', emphasis: 'muted' }),
  ].filter((row) => seriesHasValue(row.values))
  return { periods, rows }
}

const balanceSheetRollupRows = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
): { periods: string[]; rows: FinancialRollupRow[] } => {
  const { periods, start } = statementPeriods(statement, periodCount)
  const count = periods.length
  const rowFor = (candidates: string[]) => rowPeriodValues(findStatementRow(statement, candidates), start, count)
  const rows = [
    rollupRow('cash', 'Cash', rowFor(['cash', 'cash and cash equivalents', 'total cash'])),
    rollupRow('current-assets', 'Total Current Assets', rowFor(['total_current_assets', 'total current assets']), { emphasis: 'subtotal' }),
    rollupRow('assets', 'Total Assets', rowFor(['total_assets', 'total assets']), { emphasis: 'total' }),
    rollupRow('current-liabilities', 'Total Current Liabilities', rowFor(['total_current_liabilities', 'total current liabilities']), { emphasis: 'subtotal' }),
    rollupRow('liabilities', 'Total Liabilities', rowFor(['total_liabilities', 'total liabilities']), { emphasis: 'total' }),
    rollupRow('equity', 'Total Equity', rowFor(['total_equity', 'total equity']), { emphasis: 'total' }),
    rollupRow('liabilities-equity', 'Liabilities & Equity', rowFor(['total_liabilities_equity', 'total liabilities and equity']), { emphasis: 'muted' }),
  ].filter((row) => seriesHasValue(row.values))
  return { periods, rows }
}

const cashFlowRollupRows = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  periodCount: number,
): { periods: string[]; rows: FinancialRollupRow[] } => {
  const { periods, start } = statementPeriods(statement, periodCount)
  const count = periods.length
  const rowFor = (candidates: string[]) => rowPeriodValues(findStatementRow(statement, candidates), start, count)
  const rows = [
    rollupRow('beginning-cash', 'Beginning Cash', rowFor(['beginning_cash', 'beginning cash'])),
    rollupRow('operating-cash', 'Cash from Operations', rowFor(['net_cash_operating', 'net cash from operating activities', 'cash from operations', 'operating cash flow']), { emphasis: 'subtotal' }),
    rollupRow('investing-cash', 'Cash from Investing', rowFor(['net_cash_investing', 'net cash from investing activities', 'cash from investing'])),
    rollupRow('financing-cash', 'Cash from Financing', rowFor(['net_cash_financing', 'net cash from financing activities', 'cash from financing'])),
    rollupRow('net-change', 'Net Change in Cash', rowFor(['net_change_cash', 'net change in cash', 'change in cash', 'net cash change']), { emphasis: 'total' }),
    rollupRow('ending-cash', 'Ending Cash', rowFor(['ending_cash', 'ending cash balance', 'cash at end of period', 'ending cash']), { emphasis: 'total' }),
  ].filter((row) => seriesHasValue(row.values))
  return { periods, rows }
}

const metricAt = (metrics: any, path: string): number | null => {
  const value = path.split('.').reduce((acc, part) => acc?.[part], metrics)
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const formatAmount = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  const abs = Math.abs(num)
  const scaled = abs >= 1_000_000 ? num / 1_000_000 : abs >= 1_000 ? num / 1_000 : num
  const suffix = abs >= 1_000_000 ? 'M' : abs >= 1_000 ? 'K' : ''
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: abs >= 1_000 ? 1 : 0,
    maximumFractionDigits: abs >= 1_000 ? 1 : 0,
  }).format(Math.abs(scaled))
  const symbol = cfoCurrencySymbol()
  return num < 0 ? `(${symbol}${formatted}${suffix})` : `${symbol}${formatted}${suffix}`
}

const formatSignedAmount = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  if (Math.abs(num) < 0.5) return `${cfoCurrencySymbol()}0`
  return num > 0 ? `+${formatAmount(num)}` : formatAmount(num)
}

const formatFullAmount = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return ''
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: cfoCurrencyCode(),
    maximumFractionDigits: 0,
  }).format(Math.abs(num))
  return num < 0 ? `(${formatted})` : formatted
}

const formatPct = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(num * 100)}%`
}

const safeClientRatio = (numerator: any, denominator: any): number | null => {
  const num = Number(numerator)
  const den = Number(denominator)
  return Number.isFinite(num) && Number.isFinite(den) && Math.abs(den) > 0.000001 ? num / den : null
}

const formatDays = (value: number | null | undefined): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(num)} days`
}

const formatRollupValue = (value: number | null | undefined, kind: FinancialRollupRow['kind'] = 'amount'): string => (
  kind === 'percent' ? formatPct(value) : formatFullAmount(value)
)

const formatPeriod = (value: string | null | undefined): string => {
  if (!value) return ''
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}

const statementLatestPeriod = (statement: QuickBooksFpaStatementRender | null | undefined): string => {
  const periods = Array.isArray(statement?.periods) ? statement.periods : []
  return formatPeriod(periods[periods.length - 1])
}

function KpiCard({ label, value, detail, tone = 'neutral' }: { label: string; value: string; detail?: string; tone?: 'neutral' | 'good' | 'watch' }) {
  return (
    <div className="fg-cfo-presentation-kpi" data-tone={tone}>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail ? <em>{detail}</em> : null}
    </div>
  )
}

// Pinned ratio card ("Add to report" on the Ratios page). Renders from the
// shared block model so the studio preview, HTML/PDF deck, and PPTX export
// all show the same values; the live served card wins over the frozen pin.
export function RatioCardSection({
  block,
  ratioCards,
  canEdit,
}: {
  block: PresentationReportBlock
  ratioCards: unknown
  canEdit: boolean
}) {
  const model = buildRatioCardBlockModel(ratioCards, block)
  if (!model) {
    if (!canEdit) return null
    return (
      <section className="fg-cfo-presentation-ratio-card" data-report-block-id={block.id}>
        <h2>{block.title}</h2>
        <p className="fg-cfo-presentation-ratio-note">
          This ratio is no longer available. Re-add it from the Ratios &amp; trends page, or remove this block.
        </p>
      </section>
    )
  }
  const spark = buildRatioCardSparkline(model)
  return (
    <section className="fg-cfo-presentation-ratio-card" data-report-block-id={block.id} data-assessment={model.assessment}>
      <div className="fg-cfo-presentation-ratio-head">
        <h2>{block.title || model.card.label}</h2>
        <span className="fg-cfo-presentation-ratio-pill" data-assessment={model.assessment}>{model.assessmentLabel}</span>
        {model.directionLabel ? <span className="fg-cfo-presentation-ratio-direction">{model.directionLabel}</span> : null}
      </div>
      {model.explanation ? <p className="fg-cfo-presentation-ratio-reading">{model.explanation}</p> : null}
      {model.formula ? <div className="fg-cfo-presentation-ratio-formula">{model.formula}</div> : null}
      <div className="fg-cfo-presentation-kpis fg-cfo-presentation-ratio-stats">
        {model.stats.map((stat) => (
          <KpiCard key={stat.label} label={stat.label} value={stat.value} />
        ))}
      </div>
      <div className="fg-cfo-presentation-ratio-body">
        {spark ? (
          <div className="fg-cfo-presentation-ratio-spark">
            <svg viewBox={`0 0 ${spark.width} ${spark.height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`${model.card.label} 12-month trend`}>
              {spark.benchmarkY != null ? (
                <line x1={0} y1={spark.benchmarkY} x2={spark.width} y2={spark.benchmarkY} className="fg-cfo-presentation-ratio-benchline" />
              ) : null}
              <polyline points={spark.linePoints} className="fg-cfo-presentation-ratio-line" />
              {spark.points.map((point, idx) => (
                idx === 0 || idx === spark.points.length - 1
                  ? <circle key={point.period} cx={point.x} cy={point.y} r={4} className="fg-cfo-presentation-ratio-dot" />
                  : null
              ))}
              <text x={spark.firstLabel.x} y={spark.firstLabel.y} textAnchor="start" className="fg-cfo-presentation-ratio-endlabel">{spark.firstLabel.text}</text>
              <text x={spark.lastLabel.x} y={spark.lastLabel.y} textAnchor="end" className="fg-cfo-presentation-ratio-endlabel">{spark.lastLabel.text}</text>
            </svg>
            {model.benchmarkValue != null ? (
              <div className="fg-cfo-presentation-ratio-benchcaption">
                {model.benchmarkLabel}: {formatRatioCardValue(model.benchmarkValue, model.unit)}
              </div>
            ) : null}
          </div>
        ) : null}
        <table className="fg-cfo-presentation-ratio-table">
          <thead>
            <tr><th>Period</th><th>Current</th><th>Prior</th><th>Change</th></tr>
          </thead>
          <tbody>
            {model.comparisonRows.map((row, rowIdx) => (
              <tr key={`${row[0]}-${rowIdx}`}>
                {row.map((cell, idx) => <td key={`${idx}-${cell}`}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {model.note ? <p className="fg-cfo-presentation-ratio-note">{model.note}</p> : null}
    </section>
  )
}

const latestArPeriod = (analysis: QuickBooksArTrendAnalysis | null | undefined) => (
  Array.isArray(analysis?.periods) && analysis.periods.length ? analysis.periods[analysis.periods.length - 1] : null
)

const isStaleUnavailableArCommentary = (value: string): boolean => {
  const text = cleanText(value).toLowerCase()
  if (!text) return false
  return (
    text.includes('ar trend analytics are unavailable') ||
    text.includes('a/r trend analytics are unavailable') ||
    text.includes('a/r trend analysis is unavailable') ||
    text.includes('cannot provide days sales outstanding') ||
    text.includes('needs enriched accounts receivable') ||
    text.includes('no invoice/payment a/r detail')
  )
}

const numberOrNull = (value: any): number | null => {
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function AccountsReceivableSection({
  analysis,
  commentary,
  topCustomerLimit = 5,
}: {
  analysis: QuickBooksArTrendAnalysis | null | undefined
  commentary?: string
  topCustomerLimit?: number
}) {
  const latest = latestArPeriod(analysis)
  const periods = Array.isArray(analysis?.periods) ? analysis.periods : []
  const concerns = (Array.isArray(analysis?.topCustomerConcerns) ? analysis.topCustomerConcerns : []).slice(0, Math.max(1, Math.min(20, topCustomerLimit)))
  const collectability = analysis?.collectability || null
  const collectabilityCustomers = Array.isArray(collectability?.customers) ? collectability.customers : []
  const collectabilityByCustomer = new Map(collectabilityCustomers.map((customer) => [
    cleanText(customer.customerId) || cleanText(customer.customer),
    customer,
  ]))
  const thresholds = (Array.isArray(collectability?.thresholds) ? collectability.thresholds : [])
    .filter((threshold) => Number.isFinite(Number(threshold.thresholdDays)))
    .slice()
    .sort((a, b) => Number(a.thresholdDays) - Number(b.thresholdDays))
  const reserve = collectability?.badDebtReserve || null
  const atRiskOpen = numberOrNull(reserve?.atRiskOpenAmount)
    ?? (Array.isArray(collectability?.currentInvoices) ? collectability.currentInvoices : []).reduce((sum, invoice) => sum + Math.max(0, Number(invoice.openAmount || 0)), 0)
  const expectedCollectible = numberOrNull(reserve?.expectedCollectibleAmount)
  const expectedWriteOff = numberOrNull(reserve?.recommendedReserveAmount)
  const weightedRecoveryRate = safeClientRatio(expectedCollectible, atRiskOpen)
  const aiCommentaryRaw = cleanText(commentary)
  const maxDso = Math.max(1, ...periods.map((period) => Math.abs(Number(period.dso) || 0)))
  if (!latest) {
    return (
      <section className="fg-cfo-presentation-summary" data-editor-empty="true">
        <h2>A/R Trends</h2>
        <p>A/R trend analysis needs enriched Accounts Receivable GL detail with invoice due dates or payment terms.</p>
      </section>
    )
  }
  const aiCommentary = isStaleUnavailableArCommentary(aiCommentaryRaw) ? '' : aiCommentaryRaw
  return (
    <>
      {aiCommentary ? (
        <section className="fg-cfo-presentation-summary">
          <h2>A/R Commentary</h2>
          <p>{renderInlineEmphasis(aiCommentary)}</p>
        </section>
      ) : null}
      <section className="fg-cfo-presentation-kpis">
        <KpiCard label="DSO" value={formatDays(latest.dso)} detail="Invoice A/R basis" tone={Number(latest.dso || 0) > 45 ? 'watch' : 'good'} />
        <KpiCard label="Open A/R" value={formatAmount(latest.endingAr)} detail={`Average ${formatAmount(latest.averageAr)}`} />
        <KpiCard label="Overdue A/R" value={formatAmount(latest.overdueAr)} detail={formatPct(safeClientRatio(latest.overdueAr, latest.endingAr))} tone={Number(latest.overdueAr || 0) > 0 ? 'watch' : 'good'} />
        <KpiCard label="Weighted age" value={formatDays(latest.weightedAverageAge)} detail="Open invoice dollars" />
      </section>
      {collectability ? (
        <section className="fg-cfo-presentation-kpis">
          <KpiCard label="At-risk A/R" value={formatAmount(atRiskOpen)} detail="90+ day exposure" tone={Number(atRiskOpen || 0) > 0 ? 'watch' : 'good'} />
          <KpiCard label="Expected collectible" value={formatAmount(expectedCollectible)} detail={formatPct(weightedRecoveryRate)} />
          <KpiCard label="Reserve signal" value={formatAmount(expectedWriteOff)} detail={formatPct(numberOrNull(reserve?.recommendedPercentOfSales))} tone={Number(expectedWriteOff || 0) > 0 ? 'watch' : 'neutral'} />
          <KpiCard label="Benchmark sample" value={new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(collectability.sampleInvoiceCount || 0))} detail="Historical invoices" />
        </section>
      ) : null}
      <section className="fg-cfo-presentation-section">
        <div className="fg-cfo-presentation-section-head">
          <h2>DSO Trend</h2>
          <span>{formatPeriod(periods[0]?.periodStart)} to {formatPeriod(periods[periods.length - 1]?.periodEnd)}</span>
        </div>
        <div className="fg-cfo-presentation-ar-trend" style={{ gridTemplateColumns: `repeat(${Math.max(1, periods.length)}, minmax(54px, 1fr))` }}>
          {periods.map((period) => (
            <div key={period.periodStart} className="fg-cfo-presentation-ar-trend-column">
              <div className="fg-cfo-presentation-ar-trend-bar" style={{ height: `${Math.max(10, Math.abs(Number(period.dso) || 0) / maxDso * 128)}px` }} />
              <strong>{formatDays(period.dso)}</strong>
              <span>{formatPeriod(period.periodStart)}</span>
            </div>
          ))}
        </div>
      </section>
      {thresholds.length ? (
        <section className="fg-cfo-presentation-section">
          <div className="fg-cfo-presentation-section-head">
            <h2>Collectability Benchmarks</h2>
            <span>as of {formatPeriod(collectability?.asOfDate || analysis?.asOfDate)}</span>
          </div>
          <div className="fg-cfo-presentation-table-wrap">
            <table className="fg-cfo-presentation-table">
              <thead>
                <tr>
                  <th scope="col">Threshold</th>
                  <th scope="col">Open at threshold</th>
                  <th scope="col">Recovery rate</th>
                  <th scope="col">Full-pay rate</th>
                  <th scope="col">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {thresholds.map((threshold) => (
                  <tr key={threshold.thresholdDays}>
                    <th scope="row">{Number(threshold.thresholdDays).toLocaleString()}+ days</th>
                    <td>{formatFullAmount(threshold.openAtThresholdAmount)}</td>
                    <td>{formatPct(threshold.amountRecoveryRate)}</td>
                    <td>{formatPct(threshold.fullPaymentRate)}</td>
                    <td>{cleanText(threshold.confidence) || 'low'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      <section className="fg-cfo-presentation-section">
        <div className="fg-cfo-presentation-section-head">
          <h2>Top Customer Concerns</h2>
          <span>{concerns.length} customer{concerns.length === 1 ? '' : 's'}</span>
        </div>
        <div className="fg-cfo-presentation-table-wrap">
          <table className="fg-cfo-presentation-table">
            <thead>
              <tr>
                <th scope="col">Customer</th>
                <th scope="col">Open A/R</th>
                <th scope="col">Overdue</th>
                <th scope="col">90+ at risk</th>
                <th scope="col">Expected write-off</th>
                <th scope="col">DSO</th>
                <th scope="col">Weighted age</th>
              </tr>
            </thead>
            <tbody>
              {concerns.length ? concerns.map((customer) => {
                const collectabilityCustomer = collectabilityByCustomer.get(cleanText(customer.customerId) || cleanText(customer.customer))
                return (
                  <tr key={customer.customerId || customer.customer}>
                    <th scope="row">{customer.customer}</th>
                    <td>{formatFullAmount(customer.endingAr)}</td>
                    <td>{formatFullAmount(customer.overdueAr)}</td>
                    <td>{formatFullAmount(collectabilityCustomer?.over90Amount)}</td>
                    <td>{formatFullAmount(collectabilityCustomer?.expectedWriteOffAmount)}</td>
                    <td>{formatDays(customer.dso)}</td>
                    <td>{formatDays(customer.weightedAverageAge)}</td>
                  </tr>
                )
              }) : (
                <tr><td colSpan={7}>No customer concerns identified.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      {Array.isArray(analysis?.warnings) && analysis.warnings.length ? (
        <section className="fg-cfo-presentation-summary">
          <h2>A/R Data Caveats</h2>
          <p>{analysis.warnings.join(' ')}</p>
        </section>
      ) : null}
    </>
  )
}

function StatementTable({
  title,
  statement,
  limit = 18,
  periodCount = DEFAULT_PRESENTATION_PERIOD_COUNT,
}: {
  title: string
  statement: QuickBooksFpaStatementRender | null | undefined
  limit?: number
  periodCount?: number
}) {
  const boundedPeriodCount = Math.max(1, Math.min(24, Math.floor(Number(periodCount) || DEFAULT_PRESENTATION_PERIOD_COUNT)))
  const periods = Array.isArray(statement?.periods) ? statement.periods.slice(-boundedPeriodCount) : []
  const rows = visiblePresentationStatementRows(statement, limit, { collapseGroups: true })
  return (
    <section className="fg-cfo-presentation-section">
      <div className="fg-cfo-presentation-section-head">
        <h2>{title}</h2>
        {periods.length ? <span>{formatPeriod(periods[0])} to {formatPeriod(periods[periods.length - 1])}</span> : null}
      </div>
      <div className="fg-cfo-presentation-table-wrap">
        <table className="fg-cfo-presentation-table">
          <thead>
            <tr>
              <th scope="col">Line item</th>
              {periods.map((period) => <th key={period} scope="col">{formatPeriod(period)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row) => (
              <tr key={row.node_id} data-total={row.is_total ? 'true' : 'false'}>
                <th scope="row" style={{ paddingLeft: 14 + Math.max(0, Number(row.level) || 0) * 14 }}>{row.label}</th>
                {periods.map((period, periodIndex) => {
                  const start = Math.max(0, (row.values || []).length - periods.length)
                  const value = row.values?.[start + periodIndex]
                  return <td key={`${row.node_id}-${period}`}>{formatFullAmount(value == null ? undefined : Number(value))}</td>
                })}
              </tr>
            )) : (
              <tr><td colSpan={Math.max(1, periods.length + 1)}>No statement rows available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function FinancialRollupTable({
  title,
  subtitle,
  rollup,
}: {
  title: string
  subtitle?: string
  rollup: { periods: string[]; rows: FinancialRollupRow[] }
}) {
  return (
    <section className="fg-cfo-presentation-section">
      <div className="fg-cfo-presentation-section-head">
        <h2>{title}</h2>
        {subtitle ? <span>{subtitle}</span> : (
          rollup.periods.length
            ? <span>{formatPeriod(rollup.periods[0])} to {formatPeriod(rollup.periods[rollup.periods.length - 1])}</span>
            : null
        )}
      </div>
      <div className="fg-cfo-presentation-table-wrap">
        <table
          className="fg-cfo-presentation-table fg-cfo-presentation-rollup-table"
          style={{ minWidth: Math.max(860, 200 + rollup.periods.length * 78) }}
        >
          <thead>
            <tr>
              <th scope="col">Metric</th>
              {rollup.periods.map((period) => <th key={period} scope="col">{formatPeriod(period)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rollup.rows.length ? rollup.rows.map((row) => (
              <tr key={row.key} data-emphasis={row.emphasis || 'normal'}>
                <th scope="row">{row.label}</th>
                {row.values.map((value, index) => (
                  <td key={`${row.key}-${index}`}>{formatRollupValue(value, row.kind)}</td>
                ))}
              </tr>
            )) : (
              <tr><td colSpan={Math.max(1, rollup.periods.length + 1)}>No rollup rows available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function TrendBars({
  statement,
  rowLabel,
  title,
  periodCount = DEFAULT_PRESENTATION_PERIOD_COUNT,
}: {
  statement: QuickBooksFpaStatementRender | null | undefined
  rowLabel: string
  title: string
  periodCount?: number
}) {
  const { periods, start } = statementPeriods(statement, periodCount)
  const rows = Array.isArray(statement?.rows) ? statement.rows : []
  const target = rows.find((row) => cleanText(row.label).toLowerCase().includes(rowLabel.toLowerCase())) || null
  const values = rowPeriodValues(target, start, periods.length).map((value, index) => ({
    period: periods[index] || String(index + 1),
    value: Number(value) || 0,
  }))
  const max = Math.max(...values.map((entry) => Math.abs(entry.value)), 1)
  return (
    <section className="fg-cfo-presentation-section">
      <div className="fg-cfo-presentation-section-head">
        <h2>{title}</h2>
        <span>{target?.label || rowLabel}</span>
      </div>
      <div className="fg-cfo-presentation-trend" aria-label={title} style={{ gridTemplateColumns: `repeat(${Math.max(1, values.length)}, minmax(30px, 1fr))` }}>
        {values.map((entry) => (
          <div key={entry.period} className="fg-cfo-presentation-trend-column">
            <div className="fg-cfo-presentation-trend-bar" style={{ height: `${Math.max(8, Math.abs(entry.value) / max * 120)}px` }} data-negative={entry.value < 0 ? 'true' : 'false'} />
            <span>{formatPeriod(entry.period)}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

function QuarterlyRevenueNetIncomeChart({
  rollup,
}: {
  rollup: { periods: string[]; rows: FinancialRollupRow[] }
}) {
  const rowByKey = new Map(rollup.rows.map((row) => [row.key, row]))
  const quarters = new Map<string, { label: string; revenue: number; netIncome: number; ebitda: number | null; hasRevenue: boolean; hasNetIncome: boolean; hasEbitda: boolean }>()
  rollup.periods.forEach((period, index) => {
    const date = new Date(`${period}T00:00:00`)
    if (Number.isNaN(date.getTime())) return
    const quarter = Math.floor(date.getMonth() / 3) + 1
    const label = `Q${quarter} ${String(date.getFullYear()).slice(-2)}`
    const current = quarters.get(label) || { label, revenue: 0, netIncome: 0, ebitda: null, hasRevenue: false, hasNetIncome: false, hasEbitda: false }
    const revenue = Number(rowByKey.get('revenue')?.values[index])
    const netIncome = Number(rowByKey.get('net-income')?.values[index])
    const ebitda = Number(rowByKey.get('ebitda')?.values[index])
    if (Number.isFinite(revenue)) {
      current.revenue += revenue
      current.hasRevenue = true
    }
    if (Number.isFinite(netIncome)) {
      current.netIncome += netIncome
      current.hasNetIncome = true
    }
    if (Number.isFinite(ebitda)) {
      current.ebitda = (current.ebitda || 0) + ebitda
      current.hasEbitda = true
    }
    quarters.set(label, current)
  })
  const entries = Array.from(quarters.values()).filter((entry) => entry.hasRevenue || entry.hasNetIncome).slice(-QUARTERLY_TREND_MAX_QUARTERS)
  if (!entries.length) return null
  const revenueMax = Math.max(1, ...entries.map((entry) => entry.hasRevenue ? Math.max(0, entry.revenue) : 0))
  const netIncomeValues = entries
    .filter((entry) => entry.hasNetIncome && Number.isFinite(entry.netIncome))
    .map((entry) => entry.netIncome)
  const netIncomeMin = Math.min(0, ...netIncomeValues)
  const netIncomeMax = Math.max(0, ...netIncomeValues)
  const netIncomeSpan = Math.max(netIncomeMax - netIncomeMin, 1)
  const plotTop = 10
  const plotBottom = 88
  const netIncomePoints = entries
    .map((entry, index) => {
      if (!entry.hasNetIncome || !Number.isFinite(entry.netIncome)) return null
      return {
        x: ((index + 0.5) / entries.length) * 100,
        y: plotBottom - ((entry.netIncome - netIncomeMin) / netIncomeSpan) * (plotBottom - plotTop),
        value: entry.netIncome,
        label: entry.label,
      }
    })
    .filter((point): point is { x: number; y: number; value: number; label: string } => Boolean(point))
  const netIncomePath = netIncomePoints.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ')
  return (
    <section className="fg-cfo-presentation-section">
      <div className="fg-cfo-presentation-section-head">
        <h2>Quarterly Revenue & Net Income Trend</h2>
        <span>EBITDA noted as supporting context</span>
      </div>
      <div
        className="fg-cfo-presentation-quarterly"
      >
        <div
          className="fg-cfo-presentation-quarterly-plot"
          style={{
            gridTemplateColumns: `repeat(${entries.length}, minmax(92px, 1fr))`,
            minWidth: `${entries.length * 92 + Math.max(entries.length - 1, 0) * 10}px`,
          }}
        >
          <svg className="fg-cfo-presentation-quarterly-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <line className="fg-cfo-presentation-quarterly-gridline" x1="0" x2="100" y1={plotTop} y2={plotTop} />
            <line className="fg-cfo-presentation-quarterly-gridline" x1="0" x2="100" y1={(plotTop + plotBottom) / 2} y2={(plotTop + plotBottom) / 2} />
            <line className="fg-cfo-presentation-quarterly-zero" x1="0" x2="100" y1={plotBottom - ((0 - netIncomeMin) / netIncomeSpan) * (plotBottom - plotTop)} y2={plotBottom - ((0 - netIncomeMin) / netIncomeSpan) * (plotBottom - plotTop)} />
            <g data-series="net-income">
              <polyline points={netIncomePath} />
              {netIncomePoints.map((point) => (
                <circle key={`net-income-${point.label}`} cx={point.x} cy={point.y} r="1.7">
                  <title>Net income {formatAmount(point.value)} - {point.label}</title>
                </circle>
              ))}
            </g>
          </svg>
          {entries.map((entry) => (
            <div key={entry.label} className="fg-cfo-presentation-quarter-bar-column">
              <div className="fg-cfo-presentation-quarter-revenue-value">{formatAmount(entry.revenue)}</div>
              <div
                className="fg-cfo-presentation-quarter-revenue-bar"
                style={{ height: `${Math.max(5, Math.max(0, entry.revenue) / revenueMax * 100)}%` }}
                title={`Revenue ${formatAmount(entry.revenue)} - ${entry.label}`}
              />
            </div>
          ))}
        </div>
        <div
          className="fg-cfo-presentation-quarterly-periods"
          style={{
            gridTemplateColumns: `repeat(${entries.length}, minmax(92px, 1fr))`,
            minWidth: `${entries.length * 92 + Math.max(entries.length - 1, 0) * 10}px`,
          }}
        >
          {entries.map((entry) => (
            <div key={entry.label} className="fg-cfo-presentation-quarter">
              <strong>{entry.label}</strong>
              <span data-metric="net-income" data-negative={entry.netIncome < 0 ? 'true' : 'false'}>NI {formatAmount(entry.netIncome)}</span>
              {entry.hasEbitda ? <span data-metric="ebitda">EBITDA {formatAmount(entry.ebitda)}</span> : null}
            </div>
          ))}
        </div>
        <div className="fg-cfo-presentation-quarterly-legend">
          <span data-series="revenue">Revenue</span>
          <span data-series="net-income">Net income</span>
        </div>
      </div>
    </section>
  )
}

type WaterfallChartStep = {
  key: string
  label: string
  sublabel?: string
  value: number
  start: number
  end: number
  kind: 'start' | 'increase' | 'decrease' | 'end'
}

const periodYear = (period: string): number | null => {
  const match = /^(\d{4})-\d{2}-\d{2}$/.exec(period)
  return match ? Number(match[1]) : null
}

const sumSeriesRange = (values: Array<number | null> | undefined, start: number, endInclusive: number): number | null => {
  if (!values || start < 0 || endInclusive < start) return null
  let total = 0
  let hasValue = false
  for (let index = start; index <= endInclusive; index += 1) {
    const value = Number(values[index])
    if (Number.isFinite(value)) {
      total += value
      hasValue = true
    }
  }
  return hasValue ? total : null
}

// The net-income bridge's MoM/YoY deltas are sourced from the canonical
// server-side metric + period comparison engine
// (server/src/integrations/quickbooks/fpa.rs -> build_metric_comparisons,
// exposed via ServerApiClient.getQuickBooksMetricComparisons and shared via
// `serverWaterfallBridge`). This is the single comparison definition the CFO
// brief, ratio library, valuation, and workbook-wide agent all consume, so the
// presentation waterfall reads the *same* deltas rather than recomputing them
// from the rendered statement series. The statement-derived path below remains
// only for the YTD composition view (which is not a period comparison) and as
// graceful degradation when the comparison contract is unavailable.
function Waterfall({
  statement,
  metrics,
  periodCount = DEFAULT_PRESENTATION_PERIOD_COUNT,
  config,
  comparisons,
}: {
  statement: QuickBooksFpaStatementRender | null | undefined
  metrics: any
  periodCount?: number
  config?: PresentationWaterfallConfig
  comparisons?: MetricComparisonResponse | null
}) {
  const mode = config?.mode || 'mom'
  const rollup = profitAndLossRollupRows(statement, periodCount)
  const byKey = new Map(rollup.rows.map((row) => [row.key, row]))
  const latestIndex = Math.max(0, rollup.periods.length - 1)
  const latestPeriodYear = periodYear(rollup.periods[latestIndex] || '')
  const ytdStartIndex = latestPeriodYear == null
    ? latestIndex
    : Math.max(0, rollup.periods.findIndex((period) => periodYear(period) === latestPeriodYear))
  const comparisonIndex = mode === 'yoy'
    ? latestIndex - 12
    : mode === 'ytd'
      ? ytdStartIndex
      : latestIndex - 1
  const valueAt = (key: string, index: number): number | null => {
    const value = Number(byKey.get(key)?.values[index])
    return Number.isFinite(value) ? value : null
  }
  const aggregateValue = (key: string, index: number): number | null => {
    if (mode !== 'ytd') return valueAt(key, index)
    return sumSeriesRange(byKey.get(key)?.values, ytdStartIndex, latestIndex)
  }
  const serverBridge = serverWaterfallBridge(comparisons, mode)
  const latestRevenue = aggregateValue('revenue', latestIndex) ?? metricAt(metrics, 'profitAndLoss.totalRevenue') ?? 0
  const latestCost = aggregateValue('cost-of-sales', latestIndex)
  const latestOperatingExpenses = aggregateValue('operating-expenses', latestIndex) ?? metricAt(metrics, 'profitAndLoss.operatingExpenses')
  const clientLatestNetIncome = aggregateValue('net-income', latestIndex) ?? metricAt(metrics, 'profitAndLoss.netIncome') ?? 0
  const clientPriorNetIncome = comparisonIndex >= 0 ? valueAt('net-income', comparisonIndex) : null
  const hasBridge = serverBridge != null || (mode !== 'ytd' && comparisonIndex >= 0 && clientPriorNetIncome != null)
  const latestNetIncome = serverBridge ? serverBridge.latestNetIncome : clientLatestNetIncome
  const priorNetIncome = serverBridge ? serverBridge.priorNetIncome : clientPriorNetIncome
  const latestPeriod = serverBridge ? serverBridge.latestPeriod : (rollup.periods[latestIndex] || '')
  const priorPeriod = serverBridge ? serverBridge.priorPeriod : (rollup.periods[comparisonIndex] || '')
  const delta = (key: string): number | null => {
    const latest = valueAt(key, latestIndex)
    const prior = comparisonIndex >= 0 ? valueAt(key, comparisonIndex) : null
    return latest != null && prior != null ? latest - prior : null
  }
  const revenueDelta = serverBridge ? serverBridge.revenueDelta : (delta('revenue') ?? 0)
  const costDelta = serverBridge ? serverBridge.costDelta : (delta('cost-of-sales') ?? 0)
  const expenseDelta = serverBridge ? serverBridge.expenseDelta : (delta('operating-expenses') ?? 0)
  const netIncomeDelta = serverBridge ? serverBridge.netIncomeDelta : (latestNetIncome - (priorNetIncome ?? latestNetIncome))
  const otherDelta = hasBridge ? netIncomeDelta - revenueDelta + costDelta + expenseDelta : 0
  const bridgeDrivers = hasBridge
    ? [
        { key: 'revenue', label: 'Revenue', value: revenueDelta },
        { key: 'cost-of-sales', label: 'Cost of Sales', value: -costDelta },
        { key: 'operating-expenses', label: 'OpEx', sublabel: 'Operating Expenses', value: -expenseDelta },
        ...(Math.abs(otherDelta) > 1 ? [{ key: 'other', label: 'Other', sublabel: 'Other P&L Movement', value: otherDelta }] : []),
      ]
    : [
        { key: 'cost-of-sales', label: 'Cost of Sales', value: -(latestCost || 0) },
        { key: 'operating-expenses', label: 'OpEx', sublabel: 'Operating Expenses', value: -(latestOperatingExpenses || 0) },
        { key: 'other', label: 'Other', sublabel: 'Other P&L Movement', value: latestNetIncome - latestRevenue + (latestCost || 0) + (latestOperatingExpenses || 0) },
      ].filter((entry) => Math.abs(entry.value) > 1)
  const startValue = hasBridge ? (priorNetIncome || 0) : latestRevenue
  let running = startValue
  const steps: WaterfallChartStep[] = [
    {
      key: 'start',
      label: hasBridge ? formatPeriod(priorPeriod) : (mode === 'ytd' ? 'YTD Revenue' : 'Revenue'),
      sublabel: hasBridge ? 'Net Income' : (mode === 'ytd' ? formatPeriod(latestPeriod) : formatPeriod(latestPeriod)),
      value: startValue,
      start: 0,
      end: startValue,
      kind: 'start',
    },
  ]
  for (const driver of bridgeDrivers) {
    const start = running
    const end = running + driver.value
    steps.push({
      key: driver.key,
      label: driver.label,
      sublabel: driver.sublabel,
      value: driver.value,
      start,
      end,
      kind: driver.value >= 0 ? 'increase' : 'decrease',
    })
    running = end
  }
  steps.push({
    key: 'end',
    label: hasBridge ? formatPeriod(latestPeriod) : 'Net Income',
    sublabel: 'Net Income',
    value: latestNetIncome,
    start: 0,
    end: latestNetIncome,
    kind: 'end',
  })
  const allValues = steps.flatMap((step) => [step.start, step.end])
  const totals = [startValue, latestNetIncome].filter((value) => Number.isFinite(value))
  const driverMagnitude = Math.max(...bridgeDrivers.map((driver) => Math.abs(driver.value)), 1)
  const minValue = Math.min(...allValues, 0)
  const maxValue = Math.max(...allValues, 0)
  const shouldTruncatePositiveAxis = minValue >= 0 && Math.max(...totals, 1) / driverMagnitude > 4
  const yFloor = shouldTruncatePositiveAxis
    ? Math.max(0, Math.min(...allValues) - driverMagnitude * 1.8)
    : Math.min(0, minValue)
  const yCeiling = maxValue + Math.max((maxValue - yFloor) * 0.12, driverMagnitude * 0.45, 1)
  const chart = { width: 1040, height: 380, left: 58, right: 26, top: 32, bottom: 76 }
  const plotWidth = chart.width - chart.left - chart.right
  const plotHeight = chart.height - chart.top - chart.bottom
  const span = Math.max(1, yCeiling - yFloor)
  const yFor = (value: number): number => chart.top + (yCeiling - value) / span * plotHeight
  const xStep = plotWidth / Math.max(1, steps.length)
  const barWidth = Math.min(76, Math.max(42, xStep * 0.52))
  const axisTicks = Array.from({ length: 5 }, (_, index) => yFloor + (span * index) / 4)
  const barGeometry = steps.map((step, index) => {
    const x = chart.left + xStep * index + (xStep - barWidth) / 2
    const base = step.kind === 'start' || step.kind === 'end'
      ? (shouldTruncatePositiveAxis ? yFloor : 0)
      : step.start
    const topValue = Math.max(base, step.end)
    const bottomValue = Math.min(base, step.end)
    const top = yFor(topValue)
    const bottom = yFor(bottomValue)
    return {
      step,
      x,
      y: top,
      width: barWidth,
      height: Math.max(3, bottom - top),
      connectorY: yFor(step.end),
      labelY: step.kind === 'start' || step.kind === 'end'
        ? top + Math.max(3, bottom - top) / 2 + 4
        : step.value >= 0
          ? Math.max(chart.top + 12, top - 10)
          : Math.min(chart.top + plotHeight - 8, bottom + 17),
    }
  })
  const insight = hasBridge
    ? `${formatPeriod(priorPeriod)} net income ${formatAmount(priorNetIncome)} moved ${formatSignedAmount(netIncomeDelta)} to ${formatAmount(latestNetIncome)} in ${formatPeriod(latestPeriod)}.`
    : `${mode === 'ytd' ? 'Year-to-date' : formatPeriod(latestPeriod)} revenue of ${formatAmount(latestRevenue)} bridges to net income of ${formatAmount(latestNetIncome)}.`
  return (
    <section className="fg-cfo-presentation-section">
      <div className="fg-cfo-presentation-section-head">
        <h2>{config?.title || 'Net Income Bridge'}</h2>
        <span>{mode === 'yoy' ? 'Year-over-year movement' : mode === 'ytd' ? 'Year-to-date composition' : hasBridge ? 'Latest month movement' : 'Latest period composition'}</span>
      </div>
      <div className="fg-cfo-presentation-waterfall" role="img" aria-label={insight}>
        <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className="fg-cfo-presentation-waterfall-svg">
          <line
            className="fg-cfo-presentation-waterfall-axis"
            x1={chart.left}
            x2={chart.width - chart.right}
            y1={yFor(shouldTruncatePositiveAxis ? yFloor : 0)}
            y2={yFor(shouldTruncatePositiveAxis ? yFloor : 0)}
          />
          {axisTicks.map((tick) => (
            <g key={tick}>
              <line className="fg-cfo-presentation-waterfall-grid" x1={chart.left} x2={chart.width - chart.right} y1={yFor(tick)} y2={yFor(tick)} />
              <text className="fg-cfo-presentation-waterfall-tick" x={chart.left - 10} y={yFor(tick) + 4} textAnchor="end">{formatAmount(tick)}</text>
            </g>
          ))}
          {barGeometry.slice(0, -1).map((bar, index) => {
            const next = barGeometry[index + 1]
            return (
              <line
                key={`${bar.step.key}-connector`}
                className="fg-cfo-presentation-waterfall-connector"
                x1={bar.x + bar.width}
                x2={next.x}
                y1={bar.connectorY}
                y2={bar.connectorY}
              />
            )
          })}
          {barGeometry.map((bar) => (
            <g key={bar.step.key}>
              <rect
                className="fg-cfo-presentation-waterfall-column"
                data-kind={bar.step.kind}
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                rx={3}
              />
              <text
                className="fg-cfo-presentation-waterfall-value"
                data-kind={bar.step.kind}
                x={bar.x + bar.width / 2}
                y={bar.labelY}
                textAnchor="middle"
              >
                {bar.step.kind === 'start' || bar.step.kind === 'end' ? formatAmount(bar.step.value) : formatSignedAmount(bar.step.value)}
              </text>
              <text className="fg-cfo-presentation-waterfall-label" x={bar.x + bar.width / 2} y={chart.height - 34} textAnchor="middle">{bar.step.label}</text>
              {bar.step.sublabel ? (
                <text className="fg-cfo-presentation-waterfall-sublabel" x={bar.x + bar.width / 2} y={chart.height - 18} textAnchor="middle">{bar.step.sublabel}</text>
              ) : null}
            </g>
          ))}
        </svg>
        <div className="fg-cfo-presentation-waterfall-legend">
          <span><i data-kind="start" /> Prior / starting total</span>
          <span><i data-kind="increase" /> Favorable movement</span>
          <span><i data-kind="decrease" /> Unfavorable movement</span>
          <span><i data-kind="end" /> Current total</span>
        </div>
        <p className="fg-cfo-presentation-waterfall-note">{insight}</p>
      </div>
    </section>
  )
}

// Manual block editor: enable/disable, reorder (drag), add, and remove report
// blocks directly (previously only the AI agent could mutate `reportBlocks`).
// It reuses the exact `reportBlocks` config model and persists through the same
// `updateQuickBooksCfoPresentation` PUT path as the rest of the configuration,
// plus the per-audience template + theme selectors that wire `config.style`.
function BlockEditorDrawer({
  config,
  onChange,
  onClose,
  onSave,
  saving,
}: {
  config: PresentationConfig
  onChange: (next: PresentationConfig) => void
  onClose: () => void
  onSave: () => void
  saving: boolean
}) {
  const blocks = normalizeReportBlocks(config.reportBlocks)
  const style = config.style && typeof config.style === 'object' ? config.style : {}
  const resolved = resolvePresentationTheme(style)
  const [dragId, setDragId] = useState<string | null>(null)
  const [newKind, setNewKind] = useState<PresentationReportBlockKind>('text')
  const [newSection, setNewSection] = useState<PresentationSectionKey>('home')
  const [newTitle, setNewTitle] = useState('')
  const addSeqRef = useRef(0)

  const writeBlocks = (next: PresentationReportBlock[]) => {
    onChange({ ...config, reportBlocks: normalizeReportBlocks(next) })
  }
  const toggleBlock = (id: string) => {
    writeBlocks(blocks.map((block) => (block.id === id ? { ...block, enabled: block.enabled === false } : block)))
  }
  const removeBlock = (id: string) => {
    writeBlocks(blocks.filter((block) => block.id !== id))
  }
  const moveBlockByOffset = (id: string, offset: number) => {
    const index = blocks.findIndex((block) => block.id === id)
    if (index < 0) return
    const target = index + offset
    if (target < 0 || target >= blocks.length) return
    const next = blocks.slice()
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    writeBlocks(next)
  }
  const reorderToBefore = (fromId: string, toId: string) => {
    if (fromId === toId) return
    const fromIndex = blocks.findIndex((block) => block.id === fromId)
    if (fromIndex < 0) return
    const next = blocks.slice()
    const [moved] = next.splice(fromIndex, 1)
    const toIndex = next.findIndex((block) => block.id === toId)
    if (toIndex < 0) next.push(moved)
    else next.splice(toIndex, 0, moved)
    writeBlocks(next)
  }
  const addBlock = () => {
    // Monotonic per-mount counter for a stable, collision-resistant id without
    // calling an impure clock during a React update; normalizeReportBlocks also
    // de-dupes ids defensively.
    addSeqRef.current += 1
    const baseId = `custom-${newSection}-${newKind}-${blocks.length + addSeqRef.current}`
    const block: PresentationReportBlock = {
      id: baseId,
      kind: newKind,
      section: newSection,
      title: cleanText(newTitle) || reportBlockKindLabel(newKind),
      enabled: true,
      ...(newKind === 'text' ? { content: { text: '' } } : {}),
    }
    writeBlocks([...blocks, block])
    setNewTitle('')
  }
  const applyTemplate = (templateId: PresentationTemplateId) => {
    const template = PRESENTATION_TEMPLATES[templateId]
    onChange({
      ...config,
      reportBlocks: normalizeReportBlocks(applyTemplateToReportBlocks(blocks, templateId)),
      style: { ...style, template: templateId, theme: template.theme },
    })
  }
  const applyTheme = (themeId: PresentationThemeId) => {
    onChange({ ...config, style: { ...style, theme: themeId } })
  }

  return (
    <aside className="fg-cfo-presentation-config" data-testid="cfo-presentation-block-editor">
      <div className="fg-cfo-presentation-config-head">
        <div>
          <span>Report builder</span>
          <strong>Blocks, template &amp; theme</strong>
        </div>
        <button type="button" aria-label="Close report builder" onClick={onClose}><X size={16} /></button>
      </div>

      <div className="fg-cfo-presentation-config-group">
        <span>Audience template</span>
        <div className="fg-cfo-presentation-template-grid" data-testid="cfo-presentation-template-grid">
          {Object.values(PRESENTATION_TEMPLATES).map((template) => (
            <button
              key={template.id}
              type="button"
              className="fg-cfo-presentation-template-card"
              data-active={resolved.templateId === template.id ? 'true' : 'false'}
              onClick={() => applyTemplate(template.id)}
              data-testid={`cfo-presentation-template-${template.id}`}
            >
              <strong>{template.label}</strong>
              <span>{template.description}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="fg-cfo-presentation-config-group">
        <span>Theme</span>
        <label>
          <Palette size={13} /> Visual theme
          <select
            value={resolved.themeId}
            onChange={(event) => applyTheme(event.target.value as PresentationThemeId)}
            data-testid="cfo-presentation-theme-select"
          >
            {Object.values(PRESENTATION_THEMES).map((theme) => (
              <option key={theme.id} value={theme.id}>{theme.label}</option>
            ))}
          </select>
        </label>
        <p className="fg-cfo-presentation-config-hint">{PRESENTATION_THEMES[resolved.themeId].description}</p>
      </div>

      <div className="fg-cfo-presentation-config-group">
        <span>Report blocks</span>
        <p className="fg-cfo-presentation-config-hint">Drag to reorder, toggle to show/hide, or remove. Order controls how blocks stack within each section.</p>
        <ul className="fg-cfo-presentation-block-list" data-testid="cfo-presentation-block-list">
          {blocks.map((block) => (
            <li
              key={block.id}
              className="fg-cfo-presentation-block-row"
              data-enabled={block.enabled === false ? 'false' : 'true'}
              data-dragging={dragId === block.id ? 'true' : 'false'}
              draggable
              onDragStart={() => setDragId(block.id)}
              onDragEnd={() => setDragId(null)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault()
                if (dragId) reorderToBefore(dragId, block.id)
                setDragId(null)
              }}
              data-testid={`cfo-presentation-block-row-${block.id}`}
            >
              <span className="fg-cfo-presentation-block-grip" aria-hidden="true"><GripVertical size={14} /></span>
              <div className="fg-cfo-presentation-block-meta">
                <strong>{block.title}</strong>
                <span>{presentationSectionTitle(config, block.section)} · {reportBlockKindLabel(block.kind)}</span>
              </div>
              <div className="fg-cfo-presentation-block-actions">
                <button type="button" aria-label="Move up" title="Move up" onClick={() => moveBlockByOffset(block.id, -1)}><ChevronUp size={14} /></button>
                <button type="button" aria-label="Move down" title="Move down" onClick={() => moveBlockByOffset(block.id, 1)}><ChevronDown size={14} /></button>
                <button
                  type="button"
                  aria-label={block.enabled === false ? 'Show block' : 'Hide block'}
                  title={block.enabled === false ? 'Show block' : 'Hide block'}
                  data-testid={`cfo-presentation-block-toggle-${block.id}`}
                  onClick={() => toggleBlock(block.id)}
                >
                  {block.enabled === false ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button type="button" aria-label="Remove block" title="Remove block" onClick={() => removeBlock(block.id)}><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="fg-cfo-presentation-config-group">
        <span>Add a block</span>
        <div className="fg-cfo-presentation-ai-grid">
          <label>
            Type
            <select value={newKind} onChange={(event) => setNewKind(event.target.value as PresentationReportBlockKind)} data-testid="cfo-presentation-add-kind">
              {/* ratio_card is excluded: it needs a ratio id, so it is only added by pinning a card on the Ratios page. */}
              {REPORT_BLOCK_KIND_LABELS.filter((entry) => entry.kind !== 'ratio_card').map((entry) => (
                <option key={entry.kind} value={entry.kind}>{entry.label}</option>
              ))}
            </select>
          </label>
          <label>
            Section
            <select value={newSection} onChange={(event) => setNewSection(event.target.value as PresentationSectionKey)} data-testid="cfo-presentation-add-section">
              {SECTION_DEFS.map((section) => (
                <option key={section.key} value={section.key}>{presentationSectionTitle(config, section.key)}</option>
              ))}
            </select>
          </label>
        </div>
        <label>
          Title
          <input value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder={reportBlockKindLabel(newKind)} data-testid="cfo-presentation-add-title" />
        </label>
        <button type="button" onClick={addBlock} data-testid="cfo-presentation-add-block"><Plus size={14} /> Add block</button>
      </div>

      <button type="button" className="fg-cfo-presentation-primary" onClick={onSave} disabled={saving} data-testid="cfo-presentation-block-editor-save">
        <Save size={15} />
        {saving ? 'Saving...' : 'Save report'}
      </button>
    </aside>
  )
}

function ConfigDrawer({
  appApi,
  config,
  workbookId,
  savedViews,
  onChange,
  onClose,
  onSave,
  saving,
}: {
  appApi: ServerApiClient
  config: PresentationConfig
  workbookId: string | null
  savedViews: SavedStatementView[]
  onChange: (next: PresentationConfig) => void
  onClose: () => void
  onSave: () => void
  saving: boolean
}) {
  const commentary = config.commentary || {}
  const sections = config.sections || {}
  const sectionConfig = config.sectionConfig || {}
  const statementViews = config.statementViews || {}
  const branding = config.branding || {}
  const derivedMetrics = config.derivedMetrics || normalizeDerivedMetrics(null)
  const [aiConsent, setAiConsent] = useState(true)
  const [aiBusy, setAiBusy] = useState(false)
  const [aiError, setAiError] = useState('')
  const [aiDraft, setAiDraft] = useState<QuickBooksCfoPresentationAiDraftResponse | null>(null)
  const setCommentaryText = (key: keyof NonNullable<PresentationConfig['commentary']>, value: string) => {
    const nextValue = key === 'executiveSummary'
      ? value
      : value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
    onChange({
      ...config,
      commentary: {
        ...commentary,
        [key]: nextValue,
      },
    })
  }
  const updateBranding = (patch: Partial<PresentationBrandingConfig>) => {
    const nextBranding = { ...branding, ...patch }
    onChange({
      ...config,
      reportTitle: cleanText(nextBranding.reportTitle) || config.reportTitle,
      branding: nextBranding,
    })
  }
  const updateSectionConfig = (key: PresentationSectionKey, patch: Partial<PresentationSectionConfig>) => {
    const nextSection = {
      ...sectionDefault(key),
      ...(sectionConfig[key] || {}),
      ...patch,
    }
    onChange({
      ...config,
      sections: {
        ...sections,
        [key]: nextSection.enabled !== false,
      },
      sectionConfig: {
        ...sectionConfig,
        [key]: nextSection,
      },
    })
  }
  const updateEbitdaConfig = (patch: Partial<NonNullable<PresentationDerivedMetricsConfig['ebitda']>>) => {
    onChange({
      ...config,
      derivedMetrics: {
        ...derivedMetrics,
        ebitda: {
          ...derivedMetrics.ebitda,
          ...patch,
        },
      },
    })
  }
  const updateWaterfall = (id: string, patch: Partial<PresentationWaterfallConfig>) => {
    onChange({
      ...config,
      waterfalls: normalizeWaterfalls((config.waterfalls || []).map((entry) => entry.id === id ? { ...entry, ...patch } : entry)),
    })
  }
  const addWaterfall = () => {
    const count = (config.waterfalls || []).length + 1
    onChange({
      ...config,
      waterfalls: normalizeWaterfalls([
        ...(config.waterfalls || []),
        { id: `custom-waterfall-${count}`, title: `Net Income Bridge ${count}`, mode: 'mom', enabled: true, aiEnabled: true },
      ]),
    })
  }
  const removeWaterfall = (id: string) => {
    onChange({
      ...config,
      waterfalls: normalizeWaterfalls((config.waterfalls || []).filter((entry) => entry.id !== id)),
    })
  }
  const generateAiDraft = async () => {
    if (!workbookId || !aiConsent || aiBusy) return
    const aiSettings = readAiSettings()
    const provider = aiSettings.provider
    const modelId = getModelIdForProvider(aiSettings, provider)
    const requestProvider = providerForAiRequest(provider)
    setAiBusy(true)
    setAiError('')
    try {
      const draft = await appApi.generateQuickBooksCfoPresentationAiDraft(workbookId, {
        provider: requestProvider,
        model_id: requestProvider ? (cleanText(modelId) || null) : null,
        confirmed_share_evidence: true,
        sections: Object.entries(normalizeSectionConfig(sectionConfig, sections))
          .filter(([, section]) => section.aiEnabled !== false)
          .map(([id, section]) => ({
            id,
            title: section.title,
            prompt: section.defaultPrompt,
            promptHints: section.promptHints,
            promptAvoid: section.promptAvoid,
          })),
      })
      setAiDraft(draft)
      onChange({
        ...config,
        commentary: {
          ...commentary,
          executiveSummary: cleanText(draft.executiveSummary),
          sectionSummaries: Object.fromEntries(Object.entries((draft as any).sectionSummaries || {}).map(([key, value]) => [key, cleanText(value)]).filter(([, value]) => value)),
          actions: normalizeStringArray(draft.actions),
          aiMeta: {
            model_id: cleanText(draft.model_id),
            generated_at: cleanText(draft.generated_at),
            evidence_count: Array.isArray(draft.evidence) ? draft.evidence.length : 0,
            warnings: normalizeStringArray(draft.warnings),
          },
        },
      })
    } catch (err: any) {
      setAiError(cleanText(err?.message || err) || 'Could not generate an AI summary.')
    } finally {
      setAiBusy(false)
    }
  }
  const viewOptionsFor = (statementType: string): PresentationStatementView[] => [
    defaultStatementView(statementType),
    ...savedViews
      .filter((view) => view.statementType === statementType)
      .map((view) => ({
        id: view.id,
        name: view.name,
        source: 'saved',
        statement_type: statementType,
        layout_version_id: view.layoutVersionId || null,
        grain: view.grain || 'month',
        period_count: view.periodCount || config.periodCount || DEFAULT_PRESENTATION_PERIOD_COUNT,
        as_of_date: view.asOfDate || config.asOfDate,
        show_percent_of_sales: Boolean(view.showPercentOfSales),
      })),
  ]
  const updateStatementView = (statementType: string, viewId: string) => {
    const nextView = viewOptionsFor(statementType).find((view) => view.id === viewId) || defaultStatementView(statementType)
    onChange({
      ...config,
      statementViews: {
        ...statementViews,
        [statementType]: normalizeStatementView(nextView, statementType, config.periodCount || DEFAULT_PRESENTATION_PERIOD_COUNT, config.asOfDate),
      },
    })
  }
  return (
    <aside className="fg-cfo-presentation-config" data-testid="cfo-presentation-config">
      <div className="fg-cfo-presentation-config-head">
        <div>
          <span>Configure</span>
          <strong>Financial Review</strong>
        </div>
        <button type="button" aria-label="Close configuration" onClick={onClose}><X size={16} /></button>
      </div>
      <label>
        Report title
        <input
          value={branding.reportTitle || config.reportTitle || ''}
          onChange={(event) => updateBranding({ reportTitle: event.target.value })}
        />
      </label>
      <label>
        Report subtitle
        <input
          value={branding.subtitle || ''}
          onChange={(event) => updateBranding({ subtitle: event.target.value })}
          placeholder="Optional report subtitle"
        />
      </label>
      <label>
        Company display name
        <input
          value={branding.companyDisplayName || ''}
          onChange={(event) => updateBranding({ companyDisplayName: event.target.value })}
          placeholder="Use QuickBooks company name"
        />
      </label>
      <div className="fg-cfo-presentation-ai-grid">
        <label>
          Prepared report title
          <input
            value={branding.preparedReportTitle || ''}
            onChange={(event) => updateBranding({ preparedReportTitle: event.target.value })}
          />
        </label>
        <label>
          Prepared report icon
          <select
            value={branding.preparedReportIcon || 'building'}
            onChange={(event) => updateBranding({ preparedReportIcon: event.target.value })}
          >
            <option value="building">Building</option>
            <option value="file">Report</option>
            <option value="chart">Chart</option>
            <option value="cash">Cash</option>
            <option value="bank">Bank</option>
          </select>
        </label>
      </div>
      <label>
        As of date
        <input
          type="date"
          value={config.asOfDate || ''}
          onChange={(event) => onChange({ ...config, asOfDate: event.target.value || undefined })}
        />
      </label>
      <label>
        Periods
        <input
          type="number"
          min={1}
          max={24}
          value={config.periodCount || DEFAULT_PRESENTATION_PERIOD_COUNT}
          onChange={(event) => onChange({ ...config, periodCount: Math.max(1, Math.min(24, Number(event.target.value) || DEFAULT_PRESENTATION_PERIOD_COUNT)) })}
        />
      </label>
      <div className="fg-cfo-presentation-config-group">
        <span>Statement views</span>
        {STATEMENT_VIEW_KEYS.map((statementType) => {
          const options = viewOptionsFor(statementType)
          const selectedId = statementViews[statementType]?.id || '__default__'
          const label = statementType === 'profit_and_loss' ? 'Profit & Loss' : statementType === 'balance_sheet' ? 'Balance Sheet' : 'Cash Flow'
          return (
            <label key={`statement-view-${statementType}`}>
              {label}
              <select
                value={options.some((view) => view.id === selectedId) ? selectedId : '__default__'}
                onChange={(event) => updateStatementView(statementType, event.target.value)}
                data-testid={`cfo-presentation-view-${statementType}`}
              >
                {options.map((view) => (
                  <option key={`${statementType}-${view.id}`} value={view.id || '__default__'}>
                    {view.name || 'Saved view'}
                  </option>
                ))}
              </select>
            </label>
          )
        })}
      </div>
      <div className="fg-cfo-presentation-config-group">
        <span>Receivables</span>
        <label>
          Top customers
          <input
            type="number"
            min={1}
            max={20}
            value={config.receivables?.topCustomerLimit || 5}
            onChange={(event) => onChange({
              ...config,
              receivables: {
                ...(config.receivables || {}),
                topCustomerLimit: Math.max(1, Math.min(20, Math.floor(Number(event.target.value) || 5))),
              },
            })}
            data-testid="cfo-presentation-ar-top-customers"
          />
        </label>
      </div>
      <div className="fg-cfo-presentation-config-group">
        <span>Sections and AI prompts</span>
        {SECTION_DEFS.map((section) => (
          <div key={section.key} className="fg-cfo-presentation-section-config">
            <label className="fg-cfo-presentation-check">
              <input
                type="checkbox"
                checked={sectionConfig[section.key]?.enabled !== false && sections[section.key] !== false}
                onChange={(event) => updateSectionConfig(section.key, { enabled: event.target.checked })}
              />
              Show {section.label}
            </label>
            <label>
              Section title
              <input
                value={sectionConfig[section.key]?.title || section.label}
                onChange={(event) => updateSectionConfig(section.key, { title: event.target.value })}
              />
            </label>
            <label className="fg-cfo-presentation-check">
              <input
                type="checkbox"
                checked={sectionConfig[section.key]?.aiEnabled !== false}
                onChange={(event) => updateSectionConfig(section.key, { aiEnabled: event.target.checked })}
              />
              Include this section in AI analysis
            </label>
            <label>
              Default AI prompt
              <textarea
                rows={2}
                value={sectionConfig[section.key]?.defaultPrompt || sectionDefault(section.key).defaultPrompt || ''}
                onChange={(event) => updateSectionConfig(section.key, { defaultPrompt: event.target.value })}
              />
            </label>
            <label>
              Focus hints
              <textarea
                rows={2}
                value={sectionConfig[section.key]?.promptHints || ''}
                onChange={(event) => updateSectionConfig(section.key, { promptHints: event.target.value })}
                placeholder="Optional: what should AI focus on?"
              />
            </label>
            <label>
              Avoid hints
              <textarea
                rows={2}
                value={sectionConfig[section.key]?.promptAvoid || ''}
                onChange={(event) => updateSectionConfig(section.key, { promptAvoid: event.target.value })}
                placeholder="Optional: what should AI avoid?"
              />
            </label>
          </div>
        ))}
      </div>
      <div className="fg-cfo-presentation-config-group">
        <span>EBITDA</span>
        <label className="fg-cfo-presentation-check">
          <input
            type="checkbox"
            checked={derivedMetrics.ebitda?.enabled !== false}
            onChange={(event) => updateEbitdaConfig({ enabled: event.target.checked })}
          />
          Show EBITDA in P&L reporting
        </label>
        {(['interestEnabled', 'taxesEnabled', 'depreciationEnabled', 'amortizationEnabled'] as const).map((key) => (
          <label key={key} className="fg-cfo-presentation-check">
            <input
              type="checkbox"
              checked={(derivedMetrics.ebitda as any)?.[key] !== false}
              onChange={(event) => updateEbitdaConfig({ [key]: event.target.checked } as any)}
            />
            Add back {key === 'interestEnabled' ? 'interest expense' : key === 'taxesEnabled' ? 'income taxes' : key === 'depreciationEnabled' ? 'depreciation' : 'amortization'}
          </label>
        ))}
        <label>
          Additional EBITDA row hints
          <textarea
            rows={2}
            value={normalizeStringArray(derivedMetrics.ebitda?.customRowHints).join('\n')}
            onChange={(event) => updateEbitdaConfig({ customRowHints: event.target.value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean) })}
            placeholder="One row/account hint per line"
          />
        </label>
      </div>
      <div className="fg-cfo-presentation-config-group">
        <span>Net income waterfalls</span>
        {(config.waterfalls || []).map((waterfall) => (
          <div key={waterfall.id} className="fg-cfo-presentation-waterfall-config">
            <label className="fg-cfo-presentation-check">
              <input
                type="checkbox"
                checked={waterfall.enabled !== false}
                onChange={(event) => updateWaterfall(waterfall.id, { enabled: event.target.checked })}
              />
              Show waterfall
            </label>
            <label>
              Title
              <input value={waterfall.title} onChange={(event) => updateWaterfall(waterfall.id, { title: event.target.value })} />
            </label>
            <label>
              Mode
              <select value={waterfall.mode} onChange={(event) => updateWaterfall(waterfall.id, { mode: event.target.value as PresentationWaterfallMode })}>
                <option value="mom">Month over month</option>
                <option value="yoy">Year over year</option>
                <option value="ytd">Year to date</option>
              </select>
            </label>
            <label className="fg-cfo-presentation-check">
              <input
                type="checkbox"
                checked={waterfall.aiEnabled !== false}
                onChange={(event) => updateWaterfall(waterfall.id, { aiEnabled: event.target.checked })}
              />
              Include in AI analysis
            </label>
            <button type="button" onClick={() => removeWaterfall(waterfall.id)}><Trash2 size={14} /> Remove</button>
          </div>
        ))}
        <button type="button" onClick={addWaterfall}><Plus size={14} /> Add waterfall</button>
      </div>
      <label>
        Executive summary
        <textarea
          rows={5}
          value={commentary.executiveSummary || ''}
          onChange={(event) => setCommentaryText('executiveSummary', event.target.value)}
        />
      </label>
      <div className="fg-cfo-presentation-ai" data-testid="cfo-presentation-ai-controls">
        <div className="fg-cfo-presentation-ai-head">
          <span>AI draft</span>
          <strong>Generate AI Summary</strong>
        </div>
        <label className="fg-cfo-presentation-check fg-cfo-presentation-ai-consent">
          <input
            type="checkbox"
            checked={aiConsent}
            onChange={(event) => setAiConsent(event.target.checked)}
            data-testid="cfo-presentation-ai-consent"
          />
          Share the rendered financial statement workbook with the default AI model
        </label>
        <button
          type="button"
          className="fg-cfo-presentation-ai-button"
          onClick={generateAiDraft}
          disabled={!aiConsent || aiBusy}
          data-testid="cfo-presentation-ai-generate"
        >
          <Sparkles size={15} />
          {aiBusy ? 'Generating...' : 'Generate AI Summary'}
        </button>
        {aiError ? <div className="fg-cfo-presentation-ai-error" data-testid="cfo-presentation-ai-error">{aiError}</div> : null}
        {aiDraft ? (
          <div className="fg-cfo-presentation-ai-review" data-testid="cfo-presentation-ai-review">
            <strong>Draft ready for review</strong>
            <span>{aiDraft.evidence?.length || 0} evidence items | {aiDraft.model_id}</span>
            {normalizeStringArray(aiDraft.warnings).length ? (
              <ul>
                {normalizeStringArray(aiDraft.warnings).slice(0, 3).map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
      <label>
        Alerts
        <textarea
          rows={4}
          value={normalizeStringArray(commentary.alerts).join('\n')}
          onChange={(event) => setCommentaryText('alerts', event.target.value)}
        />
      </label>
      <label>
        Priorities
        <textarea
          rows={4}
          value={normalizeStringArray(commentary.actions).join('\n')}
          onChange={(event) => setCommentaryText('actions', event.target.value)}
        />
      </label>
      <label>
        Data caveats
        <textarea
          rows={3}
          value={normalizeStringArray(commentary.dataCaveats).join('\n')}
          onChange={(event) => setCommentaryText('dataCaveats', event.target.value)}
        />
      </label>
      <button type="button" className="fg-cfo-presentation-primary" onClick={onSave} disabled={saving}>
        <Save size={15} />
        {saving ? 'Saving...' : 'Save'}
      </button>
    </aside>
  )
}

export function QuickBooksCfoPresentationTab({ appApi, workbookId, isEditor = true }: Props) {
  const [response, setResponse] = useState<QuickBooksCfoPresentationResponse | null>(null)
  // Canonical metric/period comparisons (fpa.rs build_metric_comparisons). The
  // net-income waterfall reads its MoM/YoY bridge deltas from here so the
  // presentation shows the same numbers as the brief/agent/ratio surfaces.
  const [metricComparisons, setMetricComparisons] = useState<MetricComparisonResponse | null>(null)
  const [config, setConfig] = useState<PresentationConfig>(DEFAULT_CONFIG)
  const [activeSection, setActiveSection] = useState<string>('home')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [configOpen, setConfigOpen] = useState(false)
  const [layoutOpen, setLayoutOpen] = useState(false)
  const [sharingOpen, setSharingOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [savedViews, setSavedViews] = useState<SavedStatementView[]>([])
  const [agentOpen, setAgentOpen] = useState(false)
  const [agentMessage, setAgentMessage] = useState('')
  const [agentBusy, setAgentBusy] = useState(false)
  const [agentError, setAgentError] = useState('')
  const [agentTarget, setAgentTarget] = useState<PresentationAgentTarget | null>(null)
  const [lastAgentRunId, setLastAgentRunId] = useState('')
  const [lastAgentSummary, setLastAgentSummary] = useState('')
  const autoAiDraftKeyRef = useRef('')

  const applyPresentationResponse = useCallback((next: QuickBooksCfoPresentationResponse, preserveActiveSection = false) => {
    setResponse(next)
    const nextConfig = normalizeConfig(next.config)
    setConfig(nextConfig)
    const firstEnabled = SECTION_DEFS.find((section) => nextConfig.sections?.[section.key] !== false)?.key || 'home'
    setActiveSection((prev) => (
      preserveActiveSection && nextConfig.sections?.[prev] !== false
        ? prev
        : firstEnabled
    ))
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    if (!workbookId) {
      setLoading(false)
      setError('Workbook context is unavailable.')
      return () => { cancelled = true }
    }
    void (async () => {
      try {
        const next = await appApi.getQuickBooksCfoPresentation(workbookId)
        if (cancelled) return
        applyPresentationResponse(next)
      } catch (err: any) {
        if (cancelled) return
        setError(cleanText(err?.message || err) || 'Client financials are unavailable.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    // Fetch the canonical comparison set in parallel. This is the source of
    // truth for the waterfall's MoM/YoY bridge deltas; if it is unavailable the
    // waterfall degrades to the statement-derived composition, so a failure here
    // must not block or fail the presentation render.
    void (async () => {
      if (typeof appApi.getQuickBooksMetricComparisons !== 'function') return
      try {
        const cmp = await appApi.getQuickBooksMetricComparisons(workbookId, { grain: 'month' })
        if (!cancelled) setMetricComparisons(cmp)
      } catch {
        if (!cancelled) setMetricComparisons(null)
      }
    })()
    return () => { cancelled = true }
  }, [appApi, applyPresentationResponse, workbookId])

  const canEdit = Boolean(isEditor && response?.can_edit)
  useEffect(() => {
    setSavedViews(loadSavedStatementViews(cleanText(response?.connection_id)))
  }, [response?.connection_id])
  const enabledSections = useMemo(
    () => SECTION_DEFS.filter((section) => config.sections?.[section.key] !== false && config.sectionConfig?.[section.key]?.enabled !== false),
    [config.sectionConfig, config.sections],
  )
  const commentary = config.commentary || {}
  const sectionSummaries = commentary.sectionSummaries || {}
  const metrics = response?.metrics || {}
  const statements = response?.statements
  const period = statementLatestPeriod(statements?.profit_and_loss)
  const alerts = normalizeStringArray(commentary.alerts)
  const actions = normalizeStringArray(commentary.actions)
  const caveats = normalizeStringArray(commentary.dataCaveats)
  const summary = cleanText(commentary.executiveSummary)
  const selectedPeriodCount = Math.max(1, Math.min(24, Math.floor(Number(config.periodCount) || DEFAULT_PRESENTATION_PERIOD_COUNT)))
  const profitAndLossRollup = useMemo(
    () => profitAndLossRollupRows(statements?.profit_and_loss, selectedPeriodCount, config.derivedMetrics),
    [config.derivedMetrics, selectedPeriodCount, statements?.profit_and_loss],
  )
  const quarterlyProfitAndLossRollup = useMemo(
    () => profitAndLossRollupRows(statements?.profit_and_loss, QUARTERLY_TREND_MONTH_COUNT, config.derivedMetrics),
    [config.derivedMetrics, statements?.profit_and_loss],
  )
  const balanceSheetRollup = useMemo(
    () => balanceSheetRollupRows(statements?.balance_sheet, selectedPeriodCount),
    [selectedPeriodCount, statements?.balance_sheet],
  )
  const cashFlowRollup = useMemo(
    () => cashFlowRollupRows(statements?.cash_flow, selectedPeriodCount),
    [selectedPeriodCount, statements?.cash_flow],
  )
  // Resolve the active theme (config.style) into CSS custom properties so the
  // live preview renders the same palette/font the export + slide deck use.
  const theme = useMemo(() => resolvePresentationTheme(config.style), [config.style])

  useEffect(() => {
    if (!workbookId || !response || !canEdit) return
    const normalized = normalizeConfig(config)
    const existingSummary = cleanText(normalized.commentary?.executiveSummary)
    const existingActions = normalizeStringArray(normalized.commentary?.actions)
    if (existingSummary || existingActions.length) return
    const draftKey = `${workbookId}:${response.binding_id}:${response.generated_at || response.layout_version_id}`
    if (autoAiDraftKeyRef.current === draftKey) return
    autoAiDraftKeyRef.current = draftKey
    let cancelled = false
    void (async () => {
      const aiSettings = readAiSettings()
      const provider = aiSettings.provider
      const modelId = getModelIdForProvider(aiSettings, provider)
      const requestProvider = providerForAiRequest(provider)
      setStatusMessage('Drafting AI analysis...')
      try {
        const draft = await appApi.generateQuickBooksCfoPresentationAiDraft(workbookId, {
          provider: requestProvider,
          model_id: requestProvider ? (cleanText(modelId) || null) : null,
          confirmed_share_evidence: true,
          sections: Object.entries(normalizeSectionConfig(normalized.sectionConfig, normalized.sections || {}))
            .filter(([, section]) => section.aiEnabled !== false)
            .map(([id, section]) => ({
              id,
              title: section.title,
              prompt: section.defaultPrompt,
              promptHints: section.promptHints,
              promptAvoid: section.promptAvoid,
            })),
        })
        if (cancelled) return
        const nextConfig = normalizeConfig({
          ...normalized,
          commentary: {
            ...normalized.commentary,
            executiveSummary: cleanText(draft.executiveSummary),
            sectionSummaries: Object.fromEntries(Object.entries((draft as any).sectionSummaries || {}).map(([key, value]) => [key, cleanText(value)]).filter(([, value]) => value)),
            actions: normalizeStringArray(draft.actions),
            aiMeta: {
              model_id: cleanText(draft.model_id),
              generated_at: cleanText(draft.generated_at),
              evidence_count: Array.isArray(draft.evidence) ? draft.evidence.length : 0,
              warnings: normalizeStringArray(draft.warnings),
            },
          },
        })
        const next = await appApi.updateQuickBooksCfoPresentation(workbookId, { presentation: nextConfig })
        if (cancelled) return
        applyPresentationResponse(next, true)
        setStatusMessage('AI analysis drafted from the rendered financial statements.')
      } catch (err: any) {
        if (!cancelled) {
          setStatusMessage(`AI draft unavailable: ${cleanText(err?.message || err) || 'Could not generate analysis.'}`)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [appApi, applyPresentationResponse, canEdit, config, response, workbookId])

  const refreshReport = async () => {
    if (!workbookId || refreshing) return
    setRefreshing(true)
    setStatusMessage('Refreshing report...')
    try {
      const next = await appApi.getQuickBooksCfoPresentation(workbookId)
      applyPresentationResponse(next, true)
      if (typeof appApi.getQuickBooksMetricComparisons === 'function') {
        try {
          setMetricComparisons(await appApi.getQuickBooksMetricComparisons(workbookId, { grain: 'month' }))
        } catch {
          setMetricComparisons(null)
        }
      }
      setStatusMessage(`Refreshed from ${sourcePackageName(normalizeConfig(next.config), next)}.`)
    } catch (err: any) {
      setStatusMessage(`Refresh failed: ${cleanText(err?.message || err) || 'Could not refresh report.'}`)
    } finally {
      setRefreshing(false)
    }
  }

  const saveConfig = async () => {
    if (!workbookId || !canEdit) return
    setSaving(true)
    setError(null)
    try {
      const normalized = normalizeConfig(config)
      const next = await appApi.updateQuickBooksCfoPresentation(workbookId, { presentation: normalized })
      applyPresentationResponse(next, true)
      setConfigOpen(false)
    } catch (err: any) {
      setError(cleanText(err?.message || err) || 'Could not save presentation settings.')
    } finally {
      setSaving(false)
    }
  }

  // Robust print-to-PDF path. The browser's print engine is the renderer (no
  // server PDF pipeline yet — see the SERVER-PDF FOLLOW-UP note below). We try a
  // dedicated print window first; if pop-ups are blocked we fall back to a
  // hidden same-document iframe so the export still reaches the print dialog
  // reliably instead of silently failing.
  const printHtmlDocument = (html: string, safeTitle: string) => {
    const printWindow = window.open('', `${safeTitle}-pdf`, 'width=1100,height=900')
    if (printWindow) {
      printWindow.document.open()
      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.document.title = safeTitle
      const printPacket = () => {
        try {
          printWindow.focus()
          printWindow.print()
        } catch {
          setStatusMessage('PDF export could not open the print dialog.')
        }
      }
      if (printWindow.document.readyState === 'complete') {
        window.setTimeout(printPacket, 120)
      } else {
        printWindow.addEventListener('load', () => window.setTimeout(printPacket, 120), { once: true })
      }
      setStatusMessage('PDF print dialog opened. Choose "Save as PDF".')
      return
    }
    // Pop-up blocked: render into a hidden iframe and print that frame.
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.style.opacity = '0'
    document.body.appendChild(iframe)
    const doc = iframe.contentWindow?.document
    if (!doc) {
      iframe.remove()
      setStatusMessage('PDF export is unavailable in this browser context.')
      return
    }
    doc.open()
    doc.write(html)
    doc.close()
    const cleanup = () => window.setTimeout(() => iframe.remove(), 1000)
    const printFrame = () => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
        setStatusMessage('PDF print dialog opened. Choose "Save as PDF".')
      } catch {
        setStatusMessage('PDF export could not open the print dialog.')
      } finally {
        cleanup()
      }
    }
    if (doc.readyState === 'complete') {
      window.setTimeout(printFrame, 150)
    } else {
      iframe.addEventListener('load', () => window.setTimeout(printFrame, 150), { once: true })
    }
  }

  const exportPdf = async () => {
    if (!response) return
    const normalized = normalizeConfig(config)
    const html = buildQuickBooksCfoPresentationPdfHtml({
      ...response,
      config: normalized,
    }, metricComparisons)
    const reportTitle = presentationReportTitle(normalized)
    const safeTitle = reportTitle.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '') || 'financial-review'
    // Prefer the server-rendered PDF (a real application/pdf, primary path). The
    // server rasterizes the SAME themed HTML, so the output matches the live tab
    // and the HTML export. If the server has no renderer (HTTP 503 availability
    // gate) or the request fails, transparently fall back to the browser print
    // path below (approved PDF fallback).
    if (workbookId && typeof appApi.renderQuickBooksCfoPresentationPdf === 'function') {
      setStatusMessage('Rendering PDF on the server...')
      try {
        const blob = await appApi.renderQuickBooksCfoPresentationPdf(workbookId, {
          html,
          title: reportTitle,
        })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `${safeTitle}.pdf`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.setTimeout(() => URL.revokeObjectURL(url), 1000)
        setStatusMessage(`Exported ${safeTitle}.pdf.`)
        return
      } catch {
        setStatusMessage('Server PDF unavailable — opening browser print instead.')
      }
    }
    printHtmlDocument(html, safeTitle)
  }

  const openSlideDeck = () => {
    if (!response) return
    const html = buildQuickBooksCfoPresentationDeckHtml({
      ...response,
      config: normalizeConfig(config),
    }, metricComparisons)
    const safeTitle = presentationReportTitle(config).replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '') || 'financial-review'
    const deckWindow = window.open('', `${safeTitle}-deck`, 'width=1280,height=860')
    if (!deckWindow) {
      // Pop-up blocked: download the deck so it stays reachable.
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `${safeTitle}-slides.html`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
      setStatusMessage('Allow pop-ups to view slides inline — downloaded the deck instead.')
      return
    }
    deckWindow.document.open()
    deckWindow.document.write(html)
    deckWindow.document.close()
    setStatusMessage('Slide deck opened. Use arrow keys or Print for PDF.')
  }

  // SERVER-PDF (plan Section 6 area 5): `exportPdf` above now prefers the
  // server-rendered PDF route (`POST
  // /api/quickbooks/cfo/workbooks/:id/presentation/pdf`,
  // ServerApiClient.renderQuickBooksCfoPresentationPdf), which rasterizes the
  // SAME `buildQuickBooksCfoPresentationPdfHtml` output with a headless renderer
  // (Chrome/Chromium or wkhtmltopdf) and streams `application/pdf`. Per
  // AGENTS.md we do not bundle a browser or add Docker build steps, so the route
  // feature-detects a host renderer and returns a 503 availability gate when
  // none is installed; in that case (or any failure) the browser print path
  // (with a hidden-iframe fallback when pop-ups are blocked) remains as the
  // approved fallback, producing a pixel-faithful Letter PDF from the identical
  // themed HTML.

  const exportHtml = () => {
    if (!response) return
    const normalized = normalizeConfig(config)
    const html = buildQuickBooksCfoPresentationHtml({
      ...response,
      config: normalized,
    }, metricComparisons)
    const safeTitle = presentationReportTitle(normalized).replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '') || 'financial-review'
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${safeTitle}.html`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    setStatusMessage(`Exported ${safeTitle}.html.`)
  }

  if (loading) {
    return (
      <div className="fg-cfo-presentation-empty" data-testid="cfo-presentation-loading">
        Loading client financials...
      </div>
    )
  }

  if (error || !response) {
    return (
      <div className="fg-cfo-presentation-empty" data-testid="cfo-presentation-error">
        <AlertTriangle size={20} />
        <strong>Client financials are not configured.</strong>
        {canEdit ? <span>Open CFO Core and bind a QuickBooks financial statement layout.</span> : null}
        {!canEdit && error ? <span>{error}</span> : null}
      </div>
    )
  }

  const section = enabledSections.some((entry) => entry.key === activeSection)
    ? activeSection
    : (enabledSections[0]?.key || 'home')
  const activeBlocks = normalizeReportBlocks(config.reportBlocks)
    .filter((block) => block.enabled !== false && block.section === section)
  const activeSectionKey = section as PresentationSectionKey
  const defaultAgentBlock = activeBlocks[0] || null
  const selectedAgentSection = agentTarget?.section || activeSectionKey
  const selectedAgentBlockId = agentTarget?.blockId || defaultAgentBlock?.id || ''
  const selectedAgentTitle = agentTarget?.title || defaultAgentBlock?.title || presentationSectionTitle(config, selectedAgentSection)

  const openAgentForTarget = (target: PresentationAgentTarget | null) => {
    setAgentTarget(target)
    setAgentError('')
    setAgentOpen(true)
  }

  const openAgentForCurrentSection = () => {
    openAgentForTarget(defaultAgentBlock
      ? { section: defaultAgentBlock.section, blockId: defaultAgentBlock.id, title: defaultAgentBlock.title }
      : { section: activeSectionKey, blockId: '', title: presentationSectionTitle(config, activeSectionKey) })
  }

  const openAgentForBlock = (block: PresentationReportBlock) => {
    openAgentForTarget({
      section: block.section,
      blockId: block.id,
      title: block.title || presentationSectionTitle(config, block.section),
    })
  }

  const submitAgentEdit = async () => {
    if (!workbookId || !canEdit || agentBusy) return
    const message = cleanText(agentMessage)
    if (!message) {
      setAgentError('Describe the report change to apply.')
      return
    }
    const aiSettings = readAiSettings()
    const provider = aiSettings.provider
    const modelId = getModelIdForProvider(aiSettings, provider)
    const requestProvider = providerForAiRequest(provider)
    setAgentBusy(true)
    setAgentError('')
    setStatusMessage('Applying AI report edit...')
    try {
      const result = await appApi.generateQuickBooksCfoPresentationAgentEdit(workbookId, {
        provider: requestProvider,
        model_id: requestProvider ? (cleanText(modelId) || null) : null,
        confirmed_share_evidence: true,
        message,
        target_block_id: selectedAgentBlockId || null,
        target_section: selectedAgentSection,
      })
      applyPresentationResponse(result.presentation, true)
      setLastAgentRunId(cleanText(result.agent_run_id))
      setLastAgentSummary(cleanText(result.change_summary))
      setAgentMessage('')
      setAgentOpen(false)
      setStatusMessage(cleanText(result.change_summary) || 'AI report edit applied.')
    } catch (err: any) {
      const message = cleanText(err?.message || err) || 'AI report edit failed.'
      setAgentError(message)
      setStatusMessage(`AI report edit failed: ${message}`)
    } finally {
      setAgentBusy(false)
    }
  }

  const undoLastAgentEdit = async () => {
    if (!workbookId || !canEdit || !lastAgentRunId || agentBusy) return
    setAgentBusy(true)
    setAgentError('')
    setStatusMessage('Undoing AI report edit...')
    try {
      const result = await appApi.undoQuickBooksCfoPresentationAgentRun(workbookId, lastAgentRunId)
      applyPresentationResponse(result.presentation, true)
      setLastAgentRunId('')
      setLastAgentSummary('')
      setStatusMessage(cleanText(result.change_summary) || 'AI report edit undone.')
    } catch (err: any) {
      const message = cleanText(err?.message || err) || 'Could not undo AI report edit.'
      setAgentError(message)
      setStatusMessage(`Undo failed: ${message}`)
    } finally {
      setAgentBusy(false)
    }
  }

  const statementForBlock = (block: PresentationReportBlock): QuickBooksFpaStatementRender | null | undefined => {
    const statementType = cleanText(block.source?.statement_type || block.source?.statementType)
    if (statementType === 'balance_sheet') return statements?.balance_sheet
    if (statementType === 'cash_flow') return statements?.cash_flow
    return statements?.profit_and_loss
  }

  const rollupForBlock = (block: PresentationReportBlock) => {
    const statementType = cleanText(block.source?.statement_type || block.source?.statementType)
    if (statementType === 'balance_sheet') return balanceSheetRollup
    if (statementType === 'cash_flow') return cashFlowRollup
    return profitAndLossRollup
  }

  const renderKpiBlock = (block: PresentationReportBlock) => {
    if (block.section === 'balanceSheet') {
      return (
        <section className="fg-cfo-presentation-kpis" key={block.id} data-report-block-id={block.id}>
          <KpiCard label="Cash" value={formatAmount(metricAt(metrics, 'balanceSheet.cash'))} />
          <KpiCard label="Assets" value={formatAmount(metricAt(metrics, 'balanceSheet.totalAssets'))} />
          <KpiCard label="Liabilities" value={formatAmount(metricAt(metrics, 'balanceSheet.totalLiabilities'))} />
          <KpiCard label="Equity" value={formatAmount(metricAt(metrics, 'balanceSheet.totalEquity'))} />
        </section>
      )
    }
    if (block.section === 'cashFlow') {
      return (
        <section className="fg-cfo-presentation-kpis" key={block.id} data-report-block-id={block.id}>
          <KpiCard label="Net cash change" value={formatAmount(metricAt(metrics, 'cashFlow.netCashChange'))} tone={(metricAt(metrics, 'cashFlow.netCashChange') || 0) >= 0 ? 'good' : 'watch'} />
          <KpiCard label="Ending cash" value={formatAmount(metricAt(metrics, 'cashFlow.endingCash'))} />
          <KpiCard label="Revenue conversion" value={formatPct(metricAt(metrics, 'cashFlow.netCashChange') != null && metricAt(metrics, 'profitAndLoss.totalRevenue') ? (metricAt(metrics, 'cashFlow.netCashChange') || 0) / (metricAt(metrics, 'profitAndLoss.totalRevenue') || 1) : null)} />
          <KpiCard label="Runway signal" value={metricAt(metrics, 'cashFlow.endingCash') == null ? 'N/A' : ((metricAt(metrics, 'cashFlow.endingCash') || 0) >= 0 ? 'Positive' : 'Deficit')} />
        </section>
      )
    }
    return (
      <section className="fg-cfo-presentation-kpis" key={block.id} data-report-block-id={block.id}>
        <KpiCard label={block.section === 'home' ? 'Total revenue' : 'Revenue'} value={formatAmount(metricAt(metrics, 'profitAndLoss.totalRevenue'))} detail={block.section === 'home' ? 'Latest period' : undefined} tone="good" />
        <KpiCard label={block.section === 'home' ? 'Net income' : 'Gross profit'} value={formatAmount(block.section === 'home' ? metricAt(metrics, 'profitAndLoss.netIncome') : metricAt(metrics, 'profitAndLoss.grossProfit'))} detail={block.section === 'home' ? formatPct(metricAt(metrics, 'profitAndLoss.netMarginPct')) : undefined} tone={(metricAt(metrics, 'profitAndLoss.netIncome') || 0) >= 0 ? 'good' : 'watch'} />
        <KpiCard label="EBITDA" value={formatAmount(metricAt(metrics, 'profitAndLoss.ebitda') ?? profitAndLossRollup.rows.find((row) => row.key === 'ebitda')?.values.at(-1))} detail={block.section === 'home' ? 'Analytical non-GAAP metric' : undefined} />
        <KpiCard label="Gross margin" value={formatPct(metricAt(metrics, 'profitAndLoss.grossMarginPct'))} detail={block.section === 'home' ? 'Revenue quality' : undefined} />
      </section>
    )
  }

  const renderTextBlock = (block: PresentationReportBlock) => {
    const commentaryKey = cleanText(block.source?.commentary)
    const commentarySection = cleanText(block.source?.commentary_section || block.source?.commentarySection)
    const text = commentaryKey === 'executiveSummary'
      ? summary
      : commentarySection
        ? cleanText(sectionSummaries[commentarySection])
        : cleanText(block.content?.text)
    if (!text && !canEdit) return null
    return (
      <section
        key={block.id}
        className="fg-cfo-presentation-summary"
        data-editor-empty={!text ? 'true' : undefined}
        data-report-block-id={block.id}
      >
        <h2>{block.title}</h2>
        <p>{text ? renderInlineEmphasis(text) : 'Not configured'}</p>
      </section>
    )
  }

  const renderReportBlockContent = (block: PresentationReportBlock): React.ReactNode => {
    if (block.kind === 'key_highlights') {
      return <KeyHighlightsSection highlights={response?.key_highlights || []} evidence={response?.visual_evidence || []} />
    }
    if (block.kind === 'kpi_grid') return renderKpiBlock(block)
    if (block.kind === 'text') return renderTextBlock(block)
    if (block.kind === 'waterfall') {
      return (
        <React.Fragment key={block.id}>
          {normalizeWaterfalls(config.waterfalls).filter((entry) => entry.enabled !== false).map((waterfall) => (
            <Waterfall key={`${block.id}-${waterfall.id}`} statement={statements?.profit_and_loss} metrics={metrics} periodCount={selectedPeriodCount} config={waterfall} comparisons={metricComparisons} />
          ))}
        </React.Fragment>
      )
    }
    if (block.kind === 'statement_rollup') {
      return <FinancialRollupTable key={block.id} title={block.title} rollup={rollupForBlock(block)} />
    }
    if (block.kind === 'statement_detail') {
      return <StatementTable key={block.id} title={block.title} statement={statementForBlock(block)} limit={Math.max(1, Math.min(80, Math.floor(Number(block.layout?.limit) || 32)))} periodCount={selectedPeriodCount} />
    }
    if (block.kind === 'trend_chart') {
      const metric = cleanText(block.source?.metric)
      if (metric === 'quarterly_revenue_net_income') {
        return <QuarterlyRevenueNetIncomeChart key={block.id} rollup={quarterlyProfitAndLossRollup} />
      }
      return <TrendBars key={block.id} statement={statementForBlock(block)} rowLabel={cleanText(block.source?.row_label || block.source?.rowLabel) || 'net income'} title={block.title} periodCount={selectedPeriodCount} />
    }
    if (block.kind === 'ratio_card') {
      return <RatioCardSection key={block.id} block={block} ratioCards={response.ratio_cards} canEdit={canEdit} />
    }
    if (block.kind === 'ar_summary') {
      return (
        <AccountsReceivableSection
          key={block.id}
          analysis={response.ar_analysis}
          commentary={cleanText(sectionSummaries.accountsReceivable)}
          topCustomerLimit={config.receivables?.topCustomerLimit || 5}
        />
      )
    }
    if (block.kind === 'action_list') {
      return (
        <section key={block.id} className="fg-cfo-presentation-actions" data-report-block-id={block.id}>
          <div>
            <h2>Alerts</h2>
            {alerts.length ? alerts.map((item, index) => <p key={`${item}-${index}`}>{renderInlineEmphasis(item)}</p>) : (canEdit ? <p>Not configured</p> : null)}
          </div>
          <div>
            <h2>Priorities</h2>
            {actions.length ? actions.map((item, index) => <p key={`${item}-${index}`}>{renderInlineEmphasis(item)}</p>) : (canEdit ? <p>Not configured</p> : null)}
          </div>
          <div>
            <h2>Data Caveats</h2>
            {caveats.length ? caveats.map((item, index) => <p key={`${item}-${index}`}>{renderInlineEmphasis(item)}</p>) : (canEdit ? <p>Not configured</p> : null)}
            {response.warnings?.length ? response.warnings.map((warning, index) => <p key={`${warning}-${index}`}>{renderInlineEmphasis(warning)}</p>) : null}
          </div>
        </section>
      )
    }
    return null
  }

  const renderReportBlock = (block: PresentationReportBlock) => {
    const content = renderReportBlockContent(block)
    if (!content) return null
    return (
      <div
        key={block.id}
        className="fg-cfo-presentation-report-block"
        data-report-block-id={block.id}
        data-report-section={block.section}
      >
        {canEdit ? (
          <button
            type="button"
            className="fg-cfo-presentation-block-ai"
            title={`Ask AI about ${block.title}`}
            aria-label={`Ask AI about ${block.title}`}
            data-testid="cfo-presentation-block-ai"
            data-block-id={block.id}
            onClick={() => openAgentForBlock(block)}
          >
            <Sparkles size={15} />
          </button>
        ) : null}
        {content}
      </div>
    )
  }

  return (
    <div
      className="fg-cfo-presentation-shell"
      data-testid="cfo-presentation-tab"
      data-theme={theme.themeId}
      style={theme.cssVars as React.CSSProperties}
    >
      <div className="fg-cfo-presentation-toolbar">
        <nav aria-label="Client financials sections">
          {enabledSections.map((entry) => {
            const Icon = entry.icon
            return (
              <button
                key={entry.key}
                type="button"
                data-active={section === entry.key ? 'true' : 'false'}
                onClick={() => setActiveSection(entry.key)}
              >
                <Icon size={15} />
                {presentationSectionTitle(config, entry.key)}
              </button>
            )
          })}
        </nav>
        <div className="fg-cfo-presentation-toolbar-actions">
          <button
            type="button"
            onClick={refreshReport}
            disabled={refreshing}
            title={`Refresh from ${sourcePackageName(config, response)}`}
            data-testid="cfo-presentation-refresh"
          >
            <RefreshCw size={15} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button type="button" onClick={openSlideDeck} data-testid="cfo-presentation-deck">
            <Presentation size={15} />
            Slides
          </button>
          <button type="button" onClick={exportPdf} data-testid="cfo-presentation-export">
            <Printer size={15} />
            Export PDF
          </button>
          <button type="button" onClick={exportHtml} data-testid="cfo-presentation-export-html">
            <Download size={15} />
            Export HTML
          </button>
          {canEdit ? (
            <button type="button" onClick={() => setLayoutOpen(true)} data-testid="cfo-presentation-build">
              <LayoutList size={15} />
              Build report
            </button>
          ) : null}
          {canEdit ? (
            <button type="button" onClick={openAgentForCurrentSection} data-testid="cfo-presentation-agent-open">
              <MessageSquare size={15} />
              Ask AI
            </button>
          ) : null}
          {canEdit && lastAgentRunId ? (
            <button type="button" onClick={undoLastAgentEdit} disabled={agentBusy} title={lastAgentSummary || 'Undo last AI report edit'} data-testid="cfo-presentation-agent-undo">
              <RotateCcw size={15} />
              Undo AI
            </button>
          ) : null}
          {canEdit ? (
            <button type="button" onClick={() => setConfigOpen(true)} data-testid="cfo-presentation-configure">
              <Settings size={15} />
              Configure
            </button>
          ) : null}
          {canEdit ? (
            <button type="button" onClick={() => setSharingOpen(true)} data-testid="cfo-presentation-sharing">
              <Share2 size={15} />
              Share &amp; access
            </button>
          ) : null}
        </div>
      </div>
      <main className="fg-cfo-presentation-document">
        <header className="fg-cfo-presentation-masthead">
          <div>
            <span>Performance review</span>
            <h1>{presentationReportTitle(config)}</h1>
            <p>{presentationCompanyName(config, response)}{period ? ` | ${period}` : ''}{config.branding?.subtitle ? ` | ${config.branding.subtitle}` : ''}</p>
          </div>
          <div className="fg-cfo-presentation-seal">
            {React.createElement(preparedReportIconFor(config.branding?.preparedReportIcon), { size: 28 })}
            <span>{cleanText(config.branding?.preparedReportTitle) || 'Prepared report'}</span>
          </div>
        </header>

        {activeBlocks.map(renderReportBlock)}
      </main>

      {statusMessage ? <div className="fg-cfo-presentation-status">{statusMessage}</div> : null}
      {agentOpen && canEdit ? (
        <aside className="fg-cfo-presentation-agent" data-testid="cfo-presentation-agent-panel">
          <div className="fg-cfo-presentation-config-head">
            <div>
              <span>AI report editor</span>
              <strong>{presentationSectionTitle(config, section as PresentationSectionKey)}</strong>
            </div>
            <button type="button" aria-label="Close AI report editor" onClick={() => setAgentOpen(false)}><X size={16} /></button>
          </div>
          <label>
            Change request
            <textarea
              rows={5}
              value={agentMessage}
              onChange={(event) => setAgentMessage(event.target.value)}
              placeholder={`Example: refine ${selectedAgentTitle} or add supporting detail for this section`}
              data-testid="cfo-presentation-agent-message"
            />
          </label>
          <div className="fg-cfo-presentation-agent-context">
            <span>Target section</span>
            <strong>{presentationSectionTitle(config, selectedAgentSection)}</strong>
            <span>Target report</span>
            <strong>{selectedAgentTitle}</strong>
            {selectedAgentBlockId ? <em>Block: {selectedAgentBlockId}</em> : null}
          </div>
          {agentError ? <div className="fg-cfo-presentation-ai-error" data-testid="cfo-presentation-agent-error">{agentError}</div> : null}
          <button
            type="button"
            className="fg-cfo-presentation-primary"
            onClick={submitAgentEdit}
            disabled={agentBusy}
            data-testid="cfo-presentation-agent-submit"
          >
            <Sparkles size={15} />
            {agentBusy ? 'Applying...' : 'Apply AI Edit'}
          </button>
        </aside>
      ) : null}
      {layoutOpen && canEdit ? (
        <BlockEditorDrawer
          config={config}
          onChange={setConfig}
          onClose={() => setLayoutOpen(false)}
          onSave={async () => { await saveConfig(); setLayoutOpen(false) }}
          saving={saving}
        />
      ) : null}
      {sharingOpen && canEdit && workbookId ? (
        <CfoSharingPanel appApi={appApi} workbookId={workbookId} onClose={() => setSharingOpen(false)} />
      ) : null}
      {configOpen && canEdit ? (
        <ConfigDrawer
          appApi={appApi}
          config={config}
          workbookId={workbookId}
          savedViews={savedViews}
          onChange={setConfig}
          onClose={() => setConfigOpen(false)}
          onSave={saveConfig}
          saving={saving}
        />
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared studio engine surface.
//
// The calm-shell board-pack studio (`cfo/CfoPresentationsPage.tsx`) is a second
// surface that renders the SAME report blocks, derive math, themes, and config
// editors as this legacy tab. Rather than fork ~1.6k lines of proven, test-
// covered renderers/helpers (and rather than drift from the export mirror in
// `QuickBooksCfoPresentationExport.ts`), the studio imports them from here.
// This file stays the single home for the live renderers; the studio supplies
// its own calm-shell chrome + WYSIWYG preview around them.
//
// Anything exported below is part of that contract — keep it in sync with the
// studio's expectations. (`TableauCreatorLayout` still renders the tab itself.)
export {
  // Block + KPI renderers (close over no component state — pure given props).
  KpiCard,
  AccountsReceivableSection,
  StatementTable,
  FinancialRollupTable,
  TrendBars,
  QuarterlyRevenueNetIncomeChart,
  Waterfall,
  // Curate / theme / settings editors, reused as studio inspector panels.
  BlockEditorDrawer,
  ConfigDrawer,
  // Config normalizers (single source of truth for the persisted shape).
  normalizeConfig,
  normalizeReportBlocks,
  normalizeStringArray,
  normalizeSectionConfig,
  normalizeWaterfalls,
  // Derive math for the live preview.
  profitAndLossRollupRows,
  balanceSheetRollupRows,
  cashFlowRollupRows,
  metricAt,
  statementPeriods,
  findStatementRow,
  formatAmount,
  formatFullAmount,
  formatSignedAmount,
  formatPct,
  formatPeriod,
  // Presentation labelling / branding helpers.
  presentationReportTitle,
  presentationCompanyName,
  presentationSectionTitle,
  preparedReportIconFor,
  renderInlineEmphasis,
  sourcePackageName,
  statementLatestPeriod,
  loadSavedStatementViews,
  reportBlockKindLabel,
  cleanText,
  // Defaults / catalogs.
  DEFAULT_CONFIG,
  SECTION_DEFS,
  REPORT_BLOCK_KIND_LABELS,
  DEFAULT_PRESENTATION_PERIOD_COUNT,
  QUARTERLY_TREND_MONTH_COUNT,
}
export type {
  PresentationConfig,
  PresentationReportBlock,
  PresentationSectionKey,
  PresentationReportBlockKind,
  PresentationWaterfallConfig,
  PresentationAgentTarget,
  SavedStatementView,
}

export default QuickBooksCfoPresentationTab
